// Auto-generated by fetch-gen
import type { FetchClient, FetchResponse } from '@fgrzl/fetch';
import { buildQueryParams } from '@fgrzl/fetch';

/**
 * Creates an API adapter with typed methods for all OpenAPI operations.
 *
 * @param client - The FetchClient instance to use for HTTP requests
 * @returns An object with typed methods for each API operation
 *
 * @example
 * ```typescript
 * import { createAdapter } from './generated';
 * import client from '@fgrzl/fetch';
 *
 * client.setBaseUrl('https://api.example.com');
 * const api = createAdapter(client);
 *
 * const response = await api.getUsers();
 * if (response.ok) {
 *   console.log(response.data);
 * }
 * ```
 */
export function createAdapter(client: FetchClient): {
  /**
   * Compile evaluation SQL
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<evaluationSQLCompileOKResponse>
   */
  compileEvaluationSQL: (
    body: { sql?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<evaluationSQLCompileOKResponse>>;
  /**
   * Decompile evaluation rule to SQL
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<evaluationSQLDecompileResponse>
   */
  decompileEvaluationRule: (
    body: { rule?: EvaluationRule },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<evaluationSQLDecompileResponse>>;
  /**
   * Format evaluation SQL
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<evaluationSQLFormatResponse>
   */
  formatEvaluationSQL: (
    body: { sql?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<evaluationSQLFormatResponse>>;
  /**
   * List evaluation templates
   *
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<Template>>
   */
  listEvaluationTemplates: (
    query?: { tag?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<Template>>>;
  /**
   * Find an evaluation template
   *
   * @param slug - Stable template identifier (e.g. mesh.identity.suspicious_login_detected)
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Template>
   */
  findEvaluationTemplate: (
    slug: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Template>>;
  /**
   * Add an email to my profile
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EmailAddress>
   */
  addMyEmail: (
    body: { email?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EmailAddress>>;
  /**
   * Verify one of my email addresses
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EmailAddress>
   */
  verifyMyEmail: (
    body: { email?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EmailAddress>>;
  /**
   * Verify email with OTP code
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  verifyEmail: (
    body: { code?: string; email?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Update my profile name
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  updateMyName: (
    body: { first_name?: string; last_name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove my profile photo
   *
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeMyPhoto: (options?: {
    signal?: AbortSignal;
    timeout?: number;
    operationId?: string;
  }) => Promise<FetchResponse<boolean>>;
  /**
   * Add or update my profile photo
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  updateMyPhoto: (
    body: { photo_url?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List my tenants
   *
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageTenantModel>
   */
  listMyTenants: (
    query?: {
      limit?: number;
      next?: string;
      filter?: string;
      joinable?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageTenantModel>>;
  /**
   * List my permissions in a tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<string>>
   */
  listMyPermissions: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<string>>>;
  /**
   * List my roles in a tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<RoleNode>>
   */
  listMyRoles: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<RoleNode>>>;
  /**
   * List my teams in a tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageTeamModel>
   */
  listMyTeams: (
    tenant_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageTeamModel>>;
  /**
   * Find a slug
   *
   * @param slug - Unique slug identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SlugModel>
   */
  findSlug: (
    slug: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SlugModel>>;
  /**
   * Remove slug
   *
   * @param slug - Unique slug identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  surrenderSlug: (
    slug: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Check if slug can be reserved
   *
   * @param slug - Unique slug identifier
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  checkSlugReservation: (
    slug: string,
    query?: { no_cache?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Reserve a slug
   *
   * @param slug - Unique slug identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  reserveSlug: (
    slug: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Resolve an existing SSO identity by provider and subject
   *
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SSOUserIdentity>
   */
  resolveSSOUser: (
    query: { provider: string; subject: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SSOUserIdentity>>;
  /**
   * Sync a Control-owned SSO user
   *
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SSOUserIdentity>
   */
  syncSSOUser: (
    body: {
      email?: string;
      email_verified?: boolean;
      first_name?: string;
      last_name?: string;
      name?: string;
      provider?: string;
      subject?: string;
      user_id?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SSOUserIdentity>>;
  /**
   * Find a tenant by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<TenantModel>
   */
  findTenant: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<TenantModel>>;
  /**
   * Register a new tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  registerTenant: (
    tenant_id: string,
    body: { name?: string; slug?: string; tenant_id?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Create an access review campaign
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  createCampaign: (
    tenant_id: string,
    body: createCampaignRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Delete a campaign
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteCampaign: (
    tenant_id: string,
    campaign_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Edit a draft campaign
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  updateCampaignDraft: (
    tenant_id: string,
    campaign_id: string,
    body: updateCampaignRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Record accounts as under review
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<addAccountsResult>
   */
  addCampaignAccounts: (
    tenant_id: string,
    campaign_id: string,
    body: addAccountsRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<addAccountsResult>>;
  /**
   * Record a decision against a review
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param account_id - Unique identifier for the reviewed account
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  decideReview: (
    tenant_id: string,
    campaign_id: string,
    account_id: string,
    body: decisionRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Undo a decision
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param account_id - Unique identifier for the reviewed account
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  revertReviewDecision: (
    tenant_id: string,
    campaign_id: string,
    account_id: string,
    body: revertRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Complete a campaign
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  completeCampaign: (
    tenant_id: string,
    campaign_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Extend a campaign
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  extendCampaign: (
    tenant_id: string,
    campaign_id: string,
    body: extendCampaignRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Fix a campaign's population
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  resolveCampaignScope: (
    tenant_id: string,
    campaign_id: string,
    body: resolveScopeRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Begin a campaign launch
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  startCampaign: (
    tenant_id: string,
    campaign_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Give a launch up
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param campaign_id - Unique identifier for the campaign
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<campaignRef>
   */
  revertCampaignLaunch: (
    tenant_id: string,
    campaign_id: string,
    body: revertLaunchRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<campaignRef>>;
  /**
   * Create a new client
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<CreateClientResult>
   */
  createClient: (
    tenant_id: string,
    body: {
      client_id?: string;
      description?: string;
      expires_at?: string;
      name?: string;
      role_ids?: Array<string>;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<CreateClientResult>>;
  /**
   * List all clients
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<ClientModel>>
   */
  listClients: (
    tenant_id: string,
    query?: { include_revoked?: boolean },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<ClientModel>>>;
  /**
   * Find a client by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param client_id - Client identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ClientModel>
   */
  findClient: (
    tenant_id: string,
    client_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ClientModel>>;
  /**
   * Remove a client
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param client_id - Client identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeClient: (
    tenant_id: string,
    client_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Disable a client
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param client_id - Client identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  disableClient: (
    tenant_id: string,
    client_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Enable a client
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param client_id - Client identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  enableClient: (
    tenant_id: string,
    client_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Get one agent's liveness
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param client_id - Unique identifier for the client
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentPresence>
   */
  getClientPresence: (
    tenant_id: string,
    client_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentPresence>>;
  /**
   * Register client capabilities
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param client_id - Client identifier
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  registerClient: (
    tenant_id: string,
    client_id: string,
    body: { capabilities?: Array<string> },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Rotate a client's secret
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param client_id - Client identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RotateClientSecretResult>
   */
  rotateClientSecret: (
    tenant_id: string,
    client_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RotateClientSecretResult>>;
  /**
   * List downloadable client binaries
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentDownloadsResponse>
   */
  listAgentDownloads: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentDownloadsResponse>>;
  /**
   * Download client binary bundle
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param binary - Binary name (agent or agentd)
   * @param os - Target OS (linux, darwin, windows)
   * @param arch - Target architecture (amd64, arm64)
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<any>
   */
  downloadClientBundle: (
    tenant_id: string,
    binary: string,
    os: string,
    arch: string,
    query?: { client_id?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<any>>;
  /**
   * List live agents for a tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AgentFleet>
   */
  listClientPresence: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AgentFleet>>;
  /**
   * List all connectors in tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageConnectorModel>
   */
  listConnectors: (
    tenant_id: string,
    query?: {
      limit?: number;
      next?: string;
      filter?: string;
      stale_after?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageConnectorModel>>;
  /**
   * Find connector by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ConnectorModel>
   */
  findConnector: (
    tenant_id: string,
    connector_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ConnectorModel>>;
  /**
   * Create or update a connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  reconcileConnector: (
    tenant_id: string,
    connector_id: string,
    body: {
      is_system?: boolean;
      name?: string;
      provider_id?: string;
      required_sites?: Array<string>;
      requirements?: Array<string>;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeConnector: (
    tenant_id: string,
    connector_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List all features for a connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageConnectorFeatureModel>
   */
  listConnectorFeatures: (
    tenant_id: string,
    connector_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageConnectorFeatureModel>>;
  /**
   * Find connector feature by connector and feature
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ConnectorFeatureModel>
   */
  findConnectorFeature: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ConnectorFeatureModel>>;
  /**
   * ReconcileConnectorFeature
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  reconcileConnectorFeature: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    body: ReconcileConnectorFeature,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove connector feature
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeConnectorFeature: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Find connector feature contract
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ConnectorFeatureContract>
   */
  findConnectorFeatureContract: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ConnectorFeatureContract>>;
  /**
   * Unbind connector feature credential
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  unbindConnectorFeatureCredential: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    query?: { name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Get connector feature current sequence
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ConnectorFeatureInvocationSequence>
   */
  getConnectorInvocationSequence: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ConnectorFeatureInvocationSequence>>;
  /**
   * List connector feature invocation statuses
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageConnectorInvocationStatusModel>
   */
  listConnectorInvocationStatuses: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageConnectorInvocationStatusModel>>;
  /**
   * Find connector feature invocation status
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Connector feature run sequence number
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ConnectorInvocationStatusModel>
   */
  findConnectorInvocationStatus: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ConnectorInvocationStatusModel>>;
  /**
   * Record connector invocation observation
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Connector feature run sequence number
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  observeConnectorInvocation: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    body: {
      metrics?: InvocationObservationMetrics;
      phase?: string;
      processed_count?: number;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Queue connector feature invocation
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Monotonically increasing per-feature run sequence
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  queueConnectorInvocation: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    body: { run_at?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Request connector feature invocation
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Monotonically increasing per-feature run sequence
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RequestConnectorInvocationResult>
   */
  requestConnectorInvocation: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    body: { payload?: any; run_at?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RequestConnectorInvocationResult>>;
  /**
   * Get connector invocation result
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Connector feature run sequence number
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<invocationResultResponse>
   */
  getConnectorInvocationResult: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<invocationResultResponse>>;
  /**
   * Write connector invocation result
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Connector feature run sequence number
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<writeInvocationResultResponse>
   */
  writeConnectorInvocationResult: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    body: RunResultPayload,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<writeInvocationResultResponse>>;
  /**
   * Start connector feature invocation
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Connector feature run sequence number
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  startConnectorInvocation: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    body: { request_id?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Stop connector feature invocation
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Connector feature run sequence number
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  stopConnectorInvocation: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    body: { message?: string; outcome?: string; processed_count?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Refresh connector invocation liveness
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param run_sequence - Connector feature run sequence number
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  touchConnectorInvocation: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    run_sequence: number,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Get current connector feature invocation status
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ConnectorInvocationStatusModel>
   */
  getCurrentConnectorInvocationStatus: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ConnectorInvocationStatusModel>>;
  /**
   * Get connector feature resume cursor
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<resumeCursorResponse>
   */
  getConnectorFeatureResumeCursor: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<resumeCursorResponse>>;
  /**
   * Write connector feature resume cursor
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param feature_id - Provider feature template id
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  putConnectorFeatureResumeCursor: (
    tenant_id: string,
    connector_id: string,
    feature_id: string,
    body: { resume_cursor?: any; run_sequence?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List current invocation statuses by connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<ConnectorInvocationStatusModel>>
   */
  listCurrentConnectorInvocationStatusesByConnector: (
    tenant_id: string,
    connector_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<ConnectorInvocationStatusModel>>>;
  /**
   * Rename connector
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  renameConnector: (
    tenant_id: string,
    connector_id: string,
    body: { name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Purge a removed connector's catalog
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  purgeConnectorCatalog: (
    tenant_id: string,
    connector_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Add connector requirement
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  addConnectorRequirement: (
    tenant_id: string,
    connector_id: string,
    body: { requirement?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove connector requirement
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeConnectorRequirement: (
    tenant_id: string,
    connector_id: string,
    query?: { requirement?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Get Sensitive Content
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param sensitive_content_id - ID (distinct_id) of the Sensitive Content row
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SensitiveContentModel>
   */
  getSensitiveContent: (
    tenant_id: string,
    connector_id: string,
    sensitive_content_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SensitiveContentModel>>;
  /**
   * Promote Sensitive Content to a secret
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param sensitive_content_id - ID (distinct_id) of the Sensitive Content row
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SensitiveContentPromoted>
   */
  promoteSensitiveContent: (
    tenant_id: string,
    connector_id: string,
    sensitive_content_id: string,
    body: PromoteSensitiveContentRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SensitiveContentPromoted>>;
  /**
   * Get Sensitive Content value (audited)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param sensitive_content_id - ID (distinct_id) of the Sensitive Content row
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SensitiveContentValue>
   */
  getSensitiveContentValue: (
    tenant_id: string,
    connector_id: string,
    sensitive_content_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SensitiveContentValue>>;
  /**
   * Change connector status
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param connector_id - Unique identifier for the connector
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  changeConnectorStatus: (
    tenant_id: string,
    connector_id: string,
    body: { status?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List automation DLQ entries
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<DLQEntry>>
   */
  listAutomationDLQEntries: (
    tenant_id: string,
    query?: { rule_id?: string; limit?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<DLQEntry>>>;
  /**
   * Discard an automation DLQ entry
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param entry_id - Unique identifier for the automation DLQ entry
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  discardAutomationDLQEntry: (
    tenant_id: string,
    entry_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Retry an automation DLQ entry
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param entry_id - Unique identifier for the automation DLQ entry
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<DLQRetryResult>
   */
  retryAutomationDLQEntry: (
    tenant_id: string,
    entry_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<DLQRetryResult>>;
  /**
   * List automation execution audit
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<ExecutionRecord>>
   */
  listAutomationRuleExecutions: (
    tenant_id: string,
    query?: {
      rule_id?: string;
      from?: string;
      to?: string;
      status?: string;
      limit?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<ExecutionRecord>>>;
  /**
   * Preview automation action input
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ActionInputPreview>
   */
  previewAutomationActionInput: (
    tenant_id: string,
    body: PreviewActionInput,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ActionInputPreview>>;
  /**
   * Create an automation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AutomationRule>
   */
  createAutomationRule: (
    tenant_id: string,
    body: AutomationRule,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AutomationRule>>;
  /**
   * List automation rules
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<AutomationRule>>
   */
  listAutomationRules: (
    tenant_id: string,
    query?: { event_type?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<AutomationRule>>>;
  /**
   * Delete an automation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the automation rule
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteAutomationRule: (
    tenant_id: string,
    rule_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Find an automation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the automation rule
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AutomationRule>
   */
  findAutomationRule: (
    tenant_id: string,
    rule_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AutomationRule>>;
  /**
   * Update an automation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the automation rule
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AutomationRule>
   */
  updateAutomationRule: (
    tenant_id: string,
    rule_id: string,
    body: RuleUpdateInput,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AutomationRule>>;
  /**
   * Change automation rule status
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the automation rule
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  changeAutomationRuleStatus: (
    tenant_id: string,
    rule_id: string,
    body: { enabled?: boolean },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List evaluation execution audit
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<ExecutionRecord>>
   */
  listEvaluationRuleExecutions: (
    tenant_id: string,
    query?: {
      rule_id?: string;
      from?: string;
      to?: string;
      status?: string;
      limit?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<ExecutionRecord>>>;
  /**
   * Create an evaluation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EvaluationRule>
   */
  createEvaluationRule: (
    tenant_id: string,
    body: evaluationRuleCreateOpenAPIDoc,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EvaluationRule>>;
  /**
   * List evaluation rules
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<EvaluationRule>>
   */
  listEvaluationRules: (
    tenant_id: string,
    query?: { event_type?: string; event_space?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<EvaluationRule>>>;
  /**
   * Delete an evaluation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the evaluation rule
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteEvaluationRule: (
    tenant_id: string,
    rule_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Find an evaluation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the evaluation rule
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EvaluationRule>
   */
  findEvaluationRule: (
    tenant_id: string,
    rule_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EvaluationRule>>;
  /**
   * Update an evaluation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the evaluation rule
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EvaluationRule>
   */
  updateEvaluationRule: (
    tenant_id: string,
    rule_id: string,
    body: EvaluationRuleUpdateInput,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EvaluationRule>>;
  /**
   * Find evaluation rule pattern state
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the evaluation rule
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PatternStateView>
   */
  findEvaluationRulePatternState: (
    tenant_id: string,
    rule_id: string,
    query?: { correlation_id?: string; rule_version?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PatternStateView>>;
  /**
   * Publish an evaluation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the evaluation rule
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EvaluationRule>
   */
  publishEvaluationRule: (
    tenant_id: string,
    rule_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EvaluationRule>>;
  /**
   * Retire an evaluation rule
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the evaluation rule
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EvaluationRule>
   */
  retireEvaluationRule: (
    tenant_id: string,
    rule_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EvaluationRule>>;
  /**
   * Change evaluation rule status
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param rule_id - Unique identifier for the evaluation rule
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  changeEvaluationRuleStatus: (
    tenant_id: string,
    rule_id: string,
    body: { enabled?: boolean },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Fork an evaluation template
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EvaluationRule>
   */
  forkEvaluationTemplate: (
    tenant_id: string,
    body: {
      description?: string;
      enabled?: boolean;
      name?: string;
      slug?: string;
      tenant_id?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EvaluationRule>>;
  /**
   * Ingest external evaluation event
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<EventProcessingResult>
   */
  ingestEngineEvent: (
    tenant_id: string,
    body: engineEventIngestRequest,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<EventProcessingResult>>;
  /**
   * List enrollment credentials
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<EnrollmentCredentialModel>>
   */
  listEnrollmentCredentials: (
    tenant_id: string,
    query?: { kind?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<EnrollmentCredentialModel>>>;
  /**
   * Revoke an enrollment credential
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param credential_id - Credential identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  revokeEnrollmentCredential: (
    tenant_id: string,
    credential_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List a credential's enrollments
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param credential_id - Credential identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<EnrollmentCredentialUseModel>>
   */
  listEnrollmentCredentialUses: (
    tenant_id: string,
    credential_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<EnrollmentCredentialUseModel>>>;
  /**
   * Issue an enrollment join code
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<IssueEnrollmentJoinCodeResult>
   */
  issueEnrollmentJoinCode: (
    tenant_id: string,
    body: {
      capabilities?: Array<string>;
      credential_id?: string;
      expires_at?: string;
      groups?: Array<string>;
      role_ids?: Array<string>;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<IssueEnrollmentJoinCodeResult>>;
  /**
   * Create a shared enrollment secret
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<CreateEnrollmentSecretResult>
   */
  createEnrollmentSecret: (
    tenant_id: string,
    body: {
      capabilities?: Array<string>;
      credential_id?: string;
      expires_at?: string;
      groups?: Array<string>;
      name?: string;
      role_ids?: Array<string>;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<CreateEnrollmentSecretResult>>;
  /**
   * Rotate a shared enrollment secret
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param credential_id - Credential identifier
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RotateEnrollmentSecretResult>
   */
  rotateEnrollmentSecret: (
    tenant_id: string,
    credential_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RotateEnrollmentSecretResult>>;
  /**
   * Start an event_index backfill from stream origin
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RequestEventIndexBackfillResult>
   */
  backfillEventIndex: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RequestEventIndexBackfillResult>>;
  /**
   * List all member invitations for the tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageInvitationModel>
   */
  listInvitations: (
    tenant_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageInvitationModel>>;
  /**
   * Find an invitation by id
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param invitation_id - Unique identifier for the invitation
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<InvitationModel>
   */
  findInvitation: (
    tenant_id: string,
    invitation_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<InvitationModel>>;
  /**
   * Invite a member to the tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param invitation_id - Unique identifier for the invitation
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<InvitationResult>
   */
  inviteMember: (
    tenant_id: string,
    invitation_id: string,
    body: { email?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<InvitationResult>>;
  /**
   * Remove an invitation
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param invitation_id - Unique identifier for the invitation
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeInvitation: (
    tenant_id: string,
    invitation_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List running connector invocations
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RunningConnectorInvocationsPage>
   */
  listRunningConnectorInvocations: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RunningConnectorInvocationsPage>>;
  /**
   * List connector manifests
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageManifestModel>
   */
  listManifests: (
    tenant_id: string,
    query?: { limit?: number; next?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageManifestModel>>;
  /**
   * Download a connector executable
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param executable_name - Connector executable name (e.g. mesh-sample)
   * @param version - Manifest version (e.g. 0.1.0-alpha.74)
   * @param os - Target OS (e.g. darwin, linux, windows)
   * @param arch - Target architecture (e.g. amd64, arm64)
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<any>
   */
  downloadConnectorExecutable: (
    tenant_id: string,
    executable_name: string,
    version: string,
    os: string,
    arch: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<any>>;
  /**
   * Import a manifest provider and features
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param manifest_id - Provider ID from the manifest catalog
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  importManifest: (
    tenant_id: string,
    manifest_id: string,
    body: { version?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Resolve manifest for a connector feature
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Provider UUID
   * @param feature_id - Feature UUID
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Resolution>
   */
  resolveManifest: (
    tenant_id: string,
    provider_id: string,
    feature_id: string,
    query?: { version?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Resolution>>;
  /**
   * Add a member to the tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<MemberResult>
   */
  addMember: (
    tenant_id: string,
    body: { user_id?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<MemberResult>>;
  /**
   * List all members in the tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageMemberModel>
   */
  listMembers: (
    tenant_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageMemberModel>>;
  /**
   * Find a member by user id
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param user_id - Unique identifier for the user
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<MemberModel>
   */
  findMember: (
    tenant_id: string,
    user_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<MemberModel>>;
  /**
   * Remove a member from the tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param user_id - Unique identifier for the user to remove
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeMember: (
    tenant_id: string,
    user_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Update the tenant's display name
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  renameTenant: (
    tenant_id: string,
    body: { name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List all providers in tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageProviderModel>
   */
  listProviders: (
    tenant_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageProviderModel>>;
  /**
   * Find provider by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<ProviderModel>
   */
  findProvider: (
    tenant_id: string,
    provider_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<ProviderModel>>;
  /**
   * Create or update a provider
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  reconcileProvider: (
    tenant_id: string,
    provider_id: string,
    body: { description?: string; is_system?: boolean; name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove a provider
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeProvider: (
    tenant_id: string,
    provider_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List all features for a provider
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageFeatureModel>
   */
  listProviderFeatures: (
    tenant_id: string,
    provider_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageFeatureModel>>;
  /**
   * Find provider feature by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param feature_id - Unique identifier for the feature
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<FeatureModel>
   */
  findProviderFeature: (
    tenant_id: string,
    provider_id: string,
    feature_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<FeatureModel>>;
  /**
   * Create or update a provider feature
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param feature_id - Unique identifier for the feature
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  reconcileProviderFeature: (
    tenant_id: string,
    provider_id: string,
    feature_id: string,
    body: {
      credentials?: Array<CredentialRequirement>;
      description?: string;
      feature_name?: string;
      options?: any;
      payload_schema?: any;
      requirements?: Array<string>;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove a provider feature
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param feature_id - Unique identifier for the feature
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeProviderFeature: (
    tenant_id: string,
    provider_id: string,
    feature_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Add provider feature requirement
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param feature_id - Unique identifier for the feature
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  addProviderFeatureRequirement: (
    tenant_id: string,
    provider_id: string,
    feature_id: string,
    body: { requirement?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove provider feature requirement
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param feature_id - Unique identifier for the feature
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeProviderFeatureRequirement: (
    tenant_id: string,
    provider_id: string,
    feature_id: string,
    query?: { requirement?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Rename provider
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  renameProvider: (
    tenant_id: string,
    provider_id: string,
    body: { name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Add provider requirement
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  addProviderRequirement: (
    tenant_id: string,
    provider_id: string,
    body: { requirement?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove provider requirement
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeProviderRequirement: (
    tenant_id: string,
    provider_id: string,
    query?: { requirement?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Change provider status
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param provider_id - Unique identifier for the provider
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  changeProviderStatus: (
    tenant_id: string,
    provider_id: string,
    body: { status?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Start a stream-segment retention sweep
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RequestSegmentSweepResult>
   */
  sweepSegmentRetention: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RequestSegmentSweepResult>>;
  /**
   * List all roles
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageRoleModel>
   */
  listRoles: (
    tenant_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageRoleModel>>;
  /**
   * Find a specific role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Unique identifier for the role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<RoleModel>
   */
  findRole: (
    tenant_id: string,
    role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<RoleModel>>;
  /**
   * Create or update a role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Unique identifier for the role
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  reconcileRole: (
    tenant_id: string,
    role_id: string,
    body: { description?: string; name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove a role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Unique identifier for the role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeRole: (
    tenant_id: string,
    role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Get permissions for a specific role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Unique identifier for the role
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageRolePermissionModel>
   */
  listRolePermissions: (
    tenant_id: string,
    role_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageRolePermissionModel>>;
  /**
   * Grant a permission to a role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Unique identifier for the role
   * @param permission_id - Role-permission aggregate id (UUID = SHA1(role_id, permission))
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  grantRolePermission: (
    tenant_id: string,
    role_id: string,
    permission_id: string,
    body: { permission?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove a permission from a role
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param role_id - Unique identifier for the role
   * @param permission_id - Role-permission aggregate id (UUID)
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeRolePermission: (
    tenant_id: string,
    role_id: string,
    permission_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Create a new encrypted secret
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  createSecret: (
    tenant_id: string,
    body: {
      name?: string;
      raw?: any;
      secret_id?: string;
      template_id?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List all active secrets in tenant
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<SecretModel>>
   */
  listSecrets: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<SecretModel>>>;
  /**
   * Find secret metadata by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param secret_id - Unique identifier for the secret
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SecretModel>
   */
  findSecret: (
    tenant_id: string,
    secret_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SecretModel>>;
  /**
   * Remove a secret
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param secret_id - Unique identifier for the secret
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeSecret: (
    tenant_id: string,
    secret_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Update secret name
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param secret_id - Unique identifier for the secret
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  updateSecretName: (
    tenant_id: string,
    secret_id: string,
    body: { name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Access and decrypt secret value (audited)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param secret_id - Unique identifier for the secret
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<AccessSecretResult>
   */
  accessSecret: (
    tenant_id: string,
    secret_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<AccessSecretResult>>;
  /**
   * Update secret value
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param secret_id - Unique identifier for the secret
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  updateSecretValue: (
    tenant_id: string,
    secret_id: string,
    body: { raw?: any },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List secret templates
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageSecretTemplateModel>
   */
  listSecretTemplates: (
    tenant_id: string,
    query?: {
      limit?: number;
      next?: string;
      filter?: string;
      active?: boolean;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageSecretTemplateModel>>;
  /**
   * Find secret template by ID
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param template_id - Unique identifier for the template
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SecretTemplateModel>
   */
  findSecretTemplate: (
    tenant_id: string,
    template_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SecretTemplateModel>>;
  /**
   * Create or update a secret template
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param template_id - Unique identifier for the template
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  reconcileSecretTemplate: (
    tenant_id: string,
    template_id: string,
    body: { description?: string; name?: string; schema?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove a secret template
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param template_id - Unique identifier for the template
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeSecretTemplate: (
    tenant_id: string,
    template_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Initialize secrets vault (create tenant master key)
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  initializeVault: (
    tenant_id: string,
    body: { kek_key_id?: string; kek_version?: number; version?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove a tenant master key
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param version - TMK version to remove
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeTenantMasterKey: (
    tenant_id: string,
    version: number,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Deprecate a tenant master key
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deprecateTenantMasterKey: (
    tenant_id: string,
    body: { version?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Rewrap a tenant master key
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  rewrapTenantMasterKey: (
    tenant_id: string,
    body: { kek_key_id?: string; kek_version?: number; version?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List Sensitive Content
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SensitiveContentPage>
   */
  listSensitiveContent: (
    tenant_id: string,
    query?: { connector_id?: string; feature_id?: string; limit?: number },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SensitiveContentPage>>;
  /**
   * List SIEM forwarding destinations
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<DestinationModel>>
   */
  listSiemForwardingDestinations: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<DestinationModel>>>;
  /**
   * Create SIEM forwarding destination
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param destination_id - Unique identifier for the forwarding destination
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  createSiemForwardingDestination: (
    tenant_id: string,
    destination_id: string,
    body: {
      connector_id?: string;
      event_filter?: Array<string>;
      feature_name?: string;
    },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Delete SIEM forwarding destination
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param destination_id - Unique identifier for the forwarding destination
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  deleteSiemForwardingDestination: (
    tenant_id: string,
    destination_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Find SIEM forwarding destination
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param destination_id - Unique identifier for the forwarding destination
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<DestinationModel>
   */
  findSiemForwardingDestination: (
    tenant_id: string,
    destination_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<DestinationModel>>;
  /**
   * Update SIEM forwarding destination
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param destination_id - Unique identifier for the forwarding destination
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  updateSiemForwardingDestination: (
    tenant_id: string,
    destination_id: string,
    body: { event_filter?: Array<string>; feature_name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Change SIEM forwarding destination status
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param destination_id - Unique identifier for the forwarding destination
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  changeSiemForwardingDestinationStatus: (
    tenant_id: string,
    destination_id: string,
    body: { status?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List forwardable event kinds
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<Array<SiemEventKind>>
   */
  listSiemForwardingEventKinds: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<Array<SiemEventKind>>>;
  /**
   * Get SIEM forwarding settings
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<SiemForwardingSettings>
   */
  getSiemForwardingSettings: (
    tenant_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<SiemForwardingSettings>>;
  /**
   * Update the tenant's URL slug
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  changeTenantSlug: (
    tenant_id: string,
    body: { slug?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List all teams
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageTeamModel>
   */
  listTeams: (
    tenant_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageTeamModel>>;
  /**
   * Find a team by id
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<TeamModel>
   */
  findTeam: (
    tenant_id: string,
    team_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<TeamModel>>;
  /**
   * Create or update a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param body - Request body
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  reconcileTeam: (
    tenant_id: string,
    team_id: string,
    body: { description?: string; name?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Remove a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeTeam: (
    tenant_id: string,
    team_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List all members of a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageTeamMemberModel>
   */
  listTeamMembers: (
    tenant_id: string,
    team_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageTeamMemberModel>>;
  /**
   * Add a member to a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param user_id - Unique identifier for the user to add
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  assignTeamMember: (
    tenant_id: string,
    team_id: string,
    user_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Find a member of a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param user_id - Unique identifier for the user
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<TeamMemberModel>
   */
  findTeamMember: (
    tenant_id: string,
    team_id: string,
    user_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<TeamMemberModel>>;
  /**
   * Remove a member from a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param user_id - Unique identifier for the user to remove
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeTeamMember: (
    tenant_id: string,
    team_id: string,
    user_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * List roles for a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param query - Query parameters
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<PageTeamRoleModel>
   */
  listTeamRoles: (
    tenant_id: string,
    team_id: string,
    query?: { limit?: number; next?: string; filter?: string },
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<PageTeamRoleModel>>;
  /**
   * Add a role to a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param role_id - Unique identifier for the role to assign
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  assignTeamRole: (
    tenant_id: string,
    team_id: string,
    role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
  /**
   * Find a role for a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param role_id - Unique identifier for the role
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<TeamRoleModel>
   */
  findTeamRole: (
    tenant_id: string,
    team_id: string,
    role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<TeamRoleModel>>;
  /**
   * Remove a role from a team
   *
   * @param tenant_id - Unique identifier for the tenant
   * @param team_id - Unique identifier for the team
   * @param role_id - Unique identifier for the role to remove
   * @param options - Request options (signal, timeout, operationId)
   * @returns Promise resolving to FetchResponse<boolean>
   */
  removeTeamRole: (
    tenant_id: string,
    team_id: string,
    role_id: string,
    options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
  ) => Promise<FetchResponse<boolean>>;
} {
  return {
    compileEvaluationSQL: (
      body: { sql?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<evaluationSQLCompileOKResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'compileEvaluationSQL',
      };
      return client.post(
        `/api/v1/engine/evaluation/sql/compile`,
        body,
        undefined,
        finalOptions
      );
    },
    decompileEvaluationRule: (
      body: { rule?: EvaluationRule },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<evaluationSQLDecompileResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'decompileEvaluationRule',
      };
      return client.post(
        `/api/v1/engine/evaluation/sql/decompile`,
        body,
        undefined,
        finalOptions
      );
    },
    formatEvaluationSQL: (
      body: { sql?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<evaluationSQLFormatResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'formatEvaluationSQL',
      };
      return client.post(
        `/api/v1/engine/evaluation/sql/format`,
        body,
        undefined,
        finalOptions
      );
    },
    listEvaluationTemplates: (
      query?: { tag?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<Template>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listEvaluationTemplates',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/engine/evaluation/templates` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findEvaluationTemplate: (
      slug: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Template>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findEvaluationTemplate',
      };
      return client.get(
        `/api/v1/engine/evaluation/templates/${encodeURIComponent(String(slug))}`,
        undefined,
        finalOptions
      );
    },
    addMyEmail: (
      body: { email?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EmailAddress>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'addMyEmail',
      };
      return client.post(`/api/v1/me/emails`, body, undefined, finalOptions);
    },
    verifyMyEmail: (
      body: { email?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EmailAddress>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'verifyMyEmail',
      };
      return client.post(
        `/api/v1/me/emails/verification`,
        body,
        undefined,
        finalOptions
      );
    },
    verifyEmail: (
      body: { code?: string; email?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'verifyEmail',
      };
      return client.post(
        `/api/v1/me/emails/verify`,
        body,
        undefined,
        finalOptions
      );
    },
    updateMyName: (
      body: { first_name?: string; last_name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'updateMyName',
      };
      return client.put(`/api/v1/me/name`, body, undefined, finalOptions);
    },
    removeMyPhoto: (options?: {
      signal?: AbortSignal;
      timeout?: number;
      operationId?: string;
    }): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeMyPhoto',
      };
      return client.del(`/api/v1/me/photo`, undefined, finalOptions);
    },
    updateMyPhoto: (
      body: { photo_url?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'updateMyPhoto',
      };
      return client.put(`/api/v1/me/photo`, body, undefined, finalOptions);
    },
    listMyTenants: (
      query?: {
        limit?: number;
        next?: string;
        filter?: string;
        joinable?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageTenantModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listMyTenants',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url = `/api/v1/me/tenants` + (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listMyPermissions: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<string>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listMyPermissions',
      };
      return client.get(
        `/api/v1/me/tenants/${encodeURIComponent(String(tenant_id))}/permissions`,
        undefined,
        finalOptions
      );
    },
    listMyRoles: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<RoleNode>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listMyRoles',
      };
      return client.get(
        `/api/v1/me/tenants/${encodeURIComponent(String(tenant_id))}/roles`,
        undefined,
        finalOptions
      );
    },
    listMyTeams: (
      tenant_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageTeamModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listMyTeams',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/me/tenants/${encodeURIComponent(String(tenant_id))}/teams` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findSlug: (
      slug: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SlugModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findSlug',
      };
      return client.get(
        `/api/v1/slugs/${encodeURIComponent(String(slug))}`,
        undefined,
        finalOptions
      );
    },
    surrenderSlug: (
      slug: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'surrenderSlug',
      };
      return client.del(
        `/api/v1/slugs/${encodeURIComponent(String(slug))}`,
        undefined,
        finalOptions
      );
    },
    checkSlugReservation: (
      slug: string,
      query?: { no_cache?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'checkSlugReservation',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/slugs/${encodeURIComponent(String(slug))}/reservations` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    reserveSlug: (
      slug: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reserveSlug',
      };
      return client.post(
        `/api/v1/slugs/${encodeURIComponent(String(slug))}/reservations`,
        undefined,
        undefined,
        finalOptions
      );
    },
    resolveSSOUser: (
      query: { provider: string; subject: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SSOUserIdentity>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'resolveSSOUser',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/sso-users/identity` + (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    syncSSOUser: (
      body: {
        email?: string;
        email_verified?: boolean;
        first_name?: string;
        last_name?: string;
        name?: string;
        provider?: string;
        subject?: string;
        user_id?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SSOUserIdentity>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'syncSSOUser',
      };
      return client.post(
        `/api/v1/sso-users/sync`,
        body,
        undefined,
        finalOptions
      );
    },
    findTenant: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<TenantModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findTenant',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}`,
        undefined,
        finalOptions
      );
    },
    registerTenant: (
      tenant_id: string,
      body: { name?: string; slug?: string; tenant_id?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'registerTenant',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    createCampaign: (
      tenant_id: string,
      body: createCampaignRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'createCampaign',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteCampaign: (
      tenant_id: string,
      campaign_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteCampaign',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}`,
        undefined,
        finalOptions
      );
    },
    updateCampaignDraft: (
      tenant_id: string,
      campaign_id: string,
      body: updateCampaignRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'updateCampaignDraft',
      };
      return client.patch(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    addCampaignAccounts: (
      tenant_id: string,
      campaign_id: string,
      body: addAccountsRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<addAccountsResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'addCampaignAccounts',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}/accounts`,
        body,
        undefined,
        finalOptions
      );
    },
    decideReview: (
      tenant_id: string,
      campaign_id: string,
      account_id: string,
      body: decisionRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'decideReview',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}/accounts/${encodeURIComponent(String(account_id))}/decision`,
        body,
        undefined,
        finalOptions
      );
    },
    revertReviewDecision: (
      tenant_id: string,
      campaign_id: string,
      account_id: string,
      body: revertRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'revertReviewDecision',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}/accounts/${encodeURIComponent(String(account_id))}/decision/revert`,
        body,
        undefined,
        finalOptions
      );
    },
    completeCampaign: (
      tenant_id: string,
      campaign_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'completeCampaign',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}/complete`,
        undefined,
        undefined,
        finalOptions
      );
    },
    extendCampaign: (
      tenant_id: string,
      campaign_id: string,
      body: extendCampaignRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'extendCampaign',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}/extend`,
        body,
        undefined,
        finalOptions
      );
    },
    resolveCampaignScope: (
      tenant_id: string,
      campaign_id: string,
      body: resolveScopeRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'resolveCampaignScope',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}/scope`,
        body,
        undefined,
        finalOptions
      );
    },
    startCampaign: (
      tenant_id: string,
      campaign_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'startCampaign',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}/start`,
        undefined,
        undefined,
        finalOptions
      );
    },
    revertCampaignLaunch: (
      tenant_id: string,
      campaign_id: string,
      body: revertLaunchRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<campaignRef>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'revertCampaignLaunch',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/campaigns/${encodeURIComponent(String(campaign_id))}/start/revert`,
        body,
        undefined,
        finalOptions
      );
    },
    createClient: (
      tenant_id: string,
      body: {
        client_id?: string;
        description?: string;
        expires_at?: string;
        name?: string;
        role_ids?: Array<string>;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<CreateClientResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'createClient',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients`,
        body,
        undefined,
        finalOptions
      );
    },
    listClients: (
      tenant_id: string,
      query?: { include_revoked?: boolean },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<ClientModel>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listClients',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findClient: (
      tenant_id: string,
      client_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ClientModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findClient',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/${encodeURIComponent(String(client_id))}`,
        undefined,
        finalOptions
      );
    },
    removeClient: (
      tenant_id: string,
      client_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeClient',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/${encodeURIComponent(String(client_id))}`,
        undefined,
        finalOptions
      );
    },
    disableClient: (
      tenant_id: string,
      client_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'disableClient',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/${encodeURIComponent(String(client_id))}/disable`,
        undefined,
        undefined,
        finalOptions
      );
    },
    enableClient: (
      tenant_id: string,
      client_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'enableClient',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/${encodeURIComponent(String(client_id))}/enable`,
        undefined,
        undefined,
        finalOptions
      );
    },
    getClientPresence: (
      tenant_id: string,
      client_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentPresence>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getClientPresence',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/${encodeURIComponent(String(client_id))}/presence`,
        undefined,
        finalOptions
      );
    },
    registerClient: (
      tenant_id: string,
      client_id: string,
      body: { capabilities?: Array<string> },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'registerClient',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/${encodeURIComponent(String(client_id))}/register`,
        body,
        undefined,
        finalOptions
      );
    },
    rotateClientSecret: (
      tenant_id: string,
      client_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RotateClientSecretResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'rotateClientSecret',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/${encodeURIComponent(String(client_id))}/rotate`,
        undefined,
        undefined,
        finalOptions
      );
    },
    listAgentDownloads: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentDownloadsResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAgentDownloads',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/downloads`,
        undefined,
        finalOptions
      );
    },
    downloadClientBundle: (
      tenant_id: string,
      binary: string,
      os: string,
      arch: string,
      query?: { client_id?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<any>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'downloadClientBundle',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/downloads/${encodeURIComponent(String(binary))}/${encodeURIComponent(String(os))}/${encodeURIComponent(String(arch))}/bundle` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listClientPresence: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AgentFleet>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listClientPresence',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/clients/presence`,
        undefined,
        finalOptions
      );
    },
    listConnectors: (
      tenant_id: string,
      query?: {
        limit?: number;
        next?: string;
        filter?: string;
        stale_after?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageConnectorModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listConnectors',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findConnector: (
      tenant_id: string,
      connector_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ConnectorModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findConnector',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileConnector: (
      tenant_id: string,
      connector_id: string,
      body: {
        is_system?: boolean;
        name?: string;
        provider_id?: string;
        required_sites?: Array<string>;
        requirements?: Array<string>;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileConnector',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeConnector: (
      tenant_id: string,
      connector_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeConnector',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}`,
        undefined,
        finalOptions
      );
    },
    listConnectorFeatures: (
      tenant_id: string,
      connector_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageConnectorFeatureModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listConnectorFeatures',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findConnectorFeature: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ConnectorFeatureModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findConnectorFeature',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileConnectorFeature: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      body: ReconcileConnectorFeature,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileConnectorFeature',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeConnectorFeature: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeConnectorFeature',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}`,
        undefined,
        finalOptions
      );
    },
    findConnectorFeatureContract: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ConnectorFeatureContract>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findConnectorFeatureContract',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/contract`,
        undefined,
        finalOptions
      );
    },
    unbindConnectorFeatureCredential: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      query?: { name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'unbindConnectorFeatureCredential',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/credentials` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    getConnectorInvocationSequence: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ConnectorFeatureInvocationSequence>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getConnectorInvocationSequence',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/current-sequence`,
        undefined,
        finalOptions
      );
    },
    listConnectorInvocationStatuses: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageConnectorInvocationStatusModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listConnectorInvocationStatuses',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findConnectorInvocationStatus: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ConnectorInvocationStatusModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findConnectorInvocationStatus',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}`,
        undefined,
        finalOptions
      );
    },
    observeConnectorInvocation: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      body: {
        metrics?: InvocationObservationMetrics;
        phase?: string;
        processed_count?: number;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'observeConnectorInvocation',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}/observations`,
        body,
        undefined,
        finalOptions
      );
    },
    queueConnectorInvocation: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      body: { run_at?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'queueConnectorInvocation',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}/queue`,
        body,
        undefined,
        finalOptions
      );
    },
    requestConnectorInvocation: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      body: { payload?: any; run_at?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RequestConnectorInvocationResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'requestConnectorInvocation',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}/request`,
        body,
        undefined,
        finalOptions
      );
    },
    getConnectorInvocationResult: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<invocationResultResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getConnectorInvocationResult',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}/result`,
        undefined,
        finalOptions
      );
    },
    writeConnectorInvocationResult: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      body: RunResultPayload,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<writeInvocationResultResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'writeConnectorInvocationResult',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}/result`,
        body,
        undefined,
        finalOptions
      );
    },
    startConnectorInvocation: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      body: { request_id?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'startConnectorInvocation',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}/start`,
        body,
        undefined,
        finalOptions
      );
    },
    stopConnectorInvocation: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      body: { message?: string; outcome?: string; processed_count?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'stopConnectorInvocation',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}/stop`,
        body,
        undefined,
        finalOptions
      );
    },
    touchConnectorInvocation: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      run_sequence: number,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'touchConnectorInvocation',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/${encodeURIComponent(String(run_sequence))}/touch`,
        undefined,
        undefined,
        finalOptions
      );
    },
    getCurrentConnectorInvocationStatus: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ConnectorInvocationStatusModel>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'getCurrentConnectorInvocationStatus',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/invocations/current`,
        undefined,
        finalOptions
      );
    },
    getConnectorFeatureResumeCursor: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<resumeCursorResponse>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getConnectorFeatureResumeCursor',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/resume-cursor`,
        undefined,
        finalOptions
      );
    },
    putConnectorFeatureResumeCursor: (
      tenant_id: string,
      connector_id: string,
      feature_id: string,
      body: { resume_cursor?: any; run_sequence?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'putConnectorFeatureResumeCursor',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/features/${encodeURIComponent(String(feature_id))}/resume-cursor`,
        body,
        undefined,
        finalOptions
      );
    },
    listCurrentConnectorInvocationStatusesByConnector: (
      tenant_id: string,
      connector_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<ConnectorInvocationStatusModel>>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ??
          'listCurrentConnectorInvocationStatusesByConnector',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/invocations/current`,
        undefined,
        finalOptions
      );
    },
    renameConnector: (
      tenant_id: string,
      connector_id: string,
      body: { name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'renameConnector',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/name`,
        body,
        undefined,
        finalOptions
      );
    },
    purgeConnectorCatalog: (
      tenant_id: string,
      connector_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'purgeConnectorCatalog',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/purge`,
        undefined,
        undefined,
        finalOptions
      );
    },
    addConnectorRequirement: (
      tenant_id: string,
      connector_id: string,
      body: { requirement?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'addConnectorRequirement',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/requirements`,
        body,
        undefined,
        finalOptions
      );
    },
    removeConnectorRequirement: (
      tenant_id: string,
      connector_id: string,
      query?: { requirement?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeConnectorRequirement',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/requirements` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    getSensitiveContent: (
      tenant_id: string,
      connector_id: string,
      sensitive_content_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SensitiveContentModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getSensitiveContent',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/sensitive-content/${encodeURIComponent(String(sensitive_content_id))}`,
        undefined,
        finalOptions
      );
    },
    promoteSensitiveContent: (
      tenant_id: string,
      connector_id: string,
      sensitive_content_id: string,
      body: PromoteSensitiveContentRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SensitiveContentPromoted>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'promoteSensitiveContent',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/sensitive-content/${encodeURIComponent(String(sensitive_content_id))}/promote`,
        body,
        undefined,
        finalOptions
      );
    },
    getSensitiveContentValue: (
      tenant_id: string,
      connector_id: string,
      sensitive_content_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SensitiveContentValue>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getSensitiveContentValue',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/sensitive-content/${encodeURIComponent(String(sensitive_content_id))}/value`,
        undefined,
        finalOptions
      );
    },
    changeConnectorStatus: (
      tenant_id: string,
      connector_id: string,
      body: { status?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'changeConnectorStatus',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/connectors/${encodeURIComponent(String(connector_id))}/status`,
        body,
        undefined,
        finalOptions
      );
    },
    listAutomationDLQEntries: (
      tenant_id: string,
      query?: { rule_id?: string; limit?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<DLQEntry>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAutomationDLQEntries',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/dlq` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    discardAutomationDLQEntry: (
      tenant_id: string,
      entry_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'discardAutomationDLQEntry',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/dlq/${encodeURIComponent(String(entry_id))}`,
        undefined,
        finalOptions
      );
    },
    retryAutomationDLQEntry: (
      tenant_id: string,
      entry_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<DLQRetryResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'retryAutomationDLQEntry',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/dlq/${encodeURIComponent(String(entry_id))}/retry`,
        undefined,
        undefined,
        finalOptions
      );
    },
    listAutomationRuleExecutions: (
      tenant_id: string,
      query?: {
        rule_id?: string;
        from?: string;
        to?: string;
        status?: string;
        limit?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<ExecutionRecord>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAutomationRuleExecutions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/executions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    previewAutomationActionInput: (
      tenant_id: string,
      body: PreviewActionInput,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ActionInputPreview>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'previewAutomationActionInput',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/input/preview`,
        body,
        undefined,
        finalOptions
      );
    },
    createAutomationRule: (
      tenant_id: string,
      body: AutomationRule,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AutomationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'createAutomationRule',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/rules`,
        body,
        undefined,
        finalOptions
      );
    },
    listAutomationRules: (
      tenant_id: string,
      query?: { event_type?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<AutomationRule>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listAutomationRules',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/rules` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    deleteAutomationRule: (
      tenant_id: string,
      rule_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteAutomationRule',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/rules/${encodeURIComponent(String(rule_id))}`,
        undefined,
        finalOptions
      );
    },
    findAutomationRule: (
      tenant_id: string,
      rule_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AutomationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findAutomationRule',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/rules/${encodeURIComponent(String(rule_id))}`,
        undefined,
        finalOptions
      );
    },
    updateAutomationRule: (
      tenant_id: string,
      rule_id: string,
      body: RuleUpdateInput,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AutomationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'updateAutomationRule',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/rules/${encodeURIComponent(String(rule_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    changeAutomationRuleStatus: (
      tenant_id: string,
      rule_id: string,
      body: { enabled?: boolean },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'changeAutomationRuleStatus',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/automation/rules/${encodeURIComponent(String(rule_id))}/status`,
        body,
        undefined,
        finalOptions
      );
    },
    listEvaluationRuleExecutions: (
      tenant_id: string,
      query?: {
        rule_id?: string;
        from?: string;
        to?: string;
        status?: string;
        limit?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<ExecutionRecord>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listEvaluationRuleExecutions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/executions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    createEvaluationRule: (
      tenant_id: string,
      body: evaluationRuleCreateOpenAPIDoc,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EvaluationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'createEvaluationRule',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules`,
        body,
        undefined,
        finalOptions
      );
    },
    listEvaluationRules: (
      tenant_id: string,
      query?: { event_type?: string; event_space?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<EvaluationRule>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listEvaluationRules',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    deleteEvaluationRule: (
      tenant_id: string,
      rule_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteEvaluationRule',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules/${encodeURIComponent(String(rule_id))}`,
        undefined,
        finalOptions
      );
    },
    findEvaluationRule: (
      tenant_id: string,
      rule_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EvaluationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findEvaluationRule',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules/${encodeURIComponent(String(rule_id))}`,
        undefined,
        finalOptions
      );
    },
    updateEvaluationRule: (
      tenant_id: string,
      rule_id: string,
      body: EvaluationRuleUpdateInput,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EvaluationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'updateEvaluationRule',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules/${encodeURIComponent(String(rule_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    findEvaluationRulePatternState: (
      tenant_id: string,
      rule_id: string,
      query?: { correlation_id?: string; rule_version?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PatternStateView>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findEvaluationRulePatternState',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules/${encodeURIComponent(String(rule_id))}/pattern_state` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    publishEvaluationRule: (
      tenant_id: string,
      rule_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EvaluationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'publishEvaluationRule',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules/${encodeURIComponent(String(rule_id))}/publish`,
        undefined,
        undefined,
        finalOptions
      );
    },
    retireEvaluationRule: (
      tenant_id: string,
      rule_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EvaluationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'retireEvaluationRule',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules/${encodeURIComponent(String(rule_id))}/retire`,
        undefined,
        undefined,
        finalOptions
      );
    },
    changeEvaluationRuleStatus: (
      tenant_id: string,
      rule_id: string,
      body: { enabled?: boolean },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'changeEvaluationRuleStatus',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules/${encodeURIComponent(String(rule_id))}/status`,
        body,
        undefined,
        finalOptions
      );
    },
    forkEvaluationTemplate: (
      tenant_id: string,
      body: {
        description?: string;
        enabled?: boolean;
        name?: string;
        slug?: string;
        tenant_id?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EvaluationRule>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'forkEvaluationTemplate',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/evaluation/rules/fork`,
        body,
        undefined,
        finalOptions
      );
    },
    ingestEngineEvent: (
      tenant_id: string,
      body: engineEventIngestRequest,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<EventProcessingResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'ingestEngineEvent',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/engine/events`,
        body,
        undefined,
        finalOptions
      );
    },
    listEnrollmentCredentials: (
      tenant_id: string,
      query?: { kind?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<EnrollmentCredentialModel>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listEnrollmentCredentials',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/enrollment/credentials` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    revokeEnrollmentCredential: (
      tenant_id: string,
      credential_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'revokeEnrollmentCredential',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/enrollment/credentials/${encodeURIComponent(String(credential_id))}/revoke`,
        undefined,
        undefined,
        finalOptions
      );
    },
    listEnrollmentCredentialUses: (
      tenant_id: string,
      credential_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<EnrollmentCredentialUseModel>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listEnrollmentCredentialUses',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/enrollment/credentials/${encodeURIComponent(String(credential_id))}/uses`,
        undefined,
        finalOptions
      );
    },
    issueEnrollmentJoinCode: (
      tenant_id: string,
      body: {
        capabilities?: Array<string>;
        credential_id?: string;
        expires_at?: string;
        groups?: Array<string>;
        role_ids?: Array<string>;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<IssueEnrollmentJoinCodeResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'issueEnrollmentJoinCode',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/enrollment/join-codes`,
        body,
        undefined,
        finalOptions
      );
    },
    createEnrollmentSecret: (
      tenant_id: string,
      body: {
        capabilities?: Array<string>;
        credential_id?: string;
        expires_at?: string;
        groups?: Array<string>;
        name?: string;
        role_ids?: Array<string>;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<CreateEnrollmentSecretResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'createEnrollmentSecret',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/enrollment/secrets`,
        body,
        undefined,
        finalOptions
      );
    },
    rotateEnrollmentSecret: (
      tenant_id: string,
      credential_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RotateEnrollmentSecretResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'rotateEnrollmentSecret',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/enrollment/secrets/${encodeURIComponent(String(credential_id))}/rotate`,
        undefined,
        undefined,
        finalOptions
      );
    },
    backfillEventIndex: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RequestEventIndexBackfillResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'backfillEventIndex',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/events/backfill`,
        undefined,
        undefined,
        finalOptions
      );
    },
    listInvitations: (
      tenant_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageInvitationModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listInvitations',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/invitations` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findInvitation: (
      tenant_id: string,
      invitation_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<InvitationModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findInvitation',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/invitations/${encodeURIComponent(String(invitation_id))}`,
        undefined,
        finalOptions
      );
    },
    inviteMember: (
      tenant_id: string,
      invitation_id: string,
      body: { email?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<InvitationResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'inviteMember',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/invitations/${encodeURIComponent(String(invitation_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeInvitation: (
      tenant_id: string,
      invitation_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeInvitation',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/invitations/${encodeURIComponent(String(invitation_id))}`,
        undefined,
        finalOptions
      );
    },
    listRunningConnectorInvocations: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RunningConnectorInvocationsPage>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRunningConnectorInvocations',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/invocations/running`,
        undefined,
        finalOptions
      );
    },
    listManifests: (
      tenant_id: string,
      query?: { limit?: number; next?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageManifestModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listManifests',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/manifests` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    downloadConnectorExecutable: (
      tenant_id: string,
      executable_name: string,
      version: string,
      os: string,
      arch: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<any>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'downloadConnectorExecutable',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/manifests/executables/${encodeURIComponent(String(executable_name))}/${encodeURIComponent(String(version))}/${encodeURIComponent(String(os))}/${encodeURIComponent(String(arch))}`,
        undefined,
        finalOptions
      );
    },
    importManifest: (
      tenant_id: string,
      manifest_id: string,
      body: { version?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'importManifest',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/manifests/import/${encodeURIComponent(String(manifest_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    resolveManifest: (
      tenant_id: string,
      provider_id: string,
      feature_id: string,
      query?: { version?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Resolution>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'resolveManifest',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/manifests/resolve/${encodeURIComponent(String(provider_id))}/${encodeURIComponent(String(feature_id))}` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    addMember: (
      tenant_id: string,
      body: { user_id?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<MemberResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'addMember',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/members`,
        body,
        undefined,
        finalOptions
      );
    },
    listMembers: (
      tenant_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageMemberModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listMembers',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/members` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findMember: (
      tenant_id: string,
      user_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<MemberModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findMember',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/members/${encodeURIComponent(String(user_id))}`,
        undefined,
        finalOptions
      );
    },
    removeMember: (
      tenant_id: string,
      user_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeMember',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/members/${encodeURIComponent(String(user_id))}`,
        undefined,
        finalOptions
      );
    },
    renameTenant: (
      tenant_id: string,
      body: { name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'renameTenant',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/name`,
        body,
        undefined,
        finalOptions
      );
    },
    listProviders: (
      tenant_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageProviderModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listProviders',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findProvider: (
      tenant_id: string,
      provider_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<ProviderModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findProvider',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileProvider: (
      tenant_id: string,
      provider_id: string,
      body: { description?: string; is_system?: boolean; name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileProvider',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeProvider: (
      tenant_id: string,
      provider_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeProvider',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}`,
        undefined,
        finalOptions
      );
    },
    listProviderFeatures: (
      tenant_id: string,
      provider_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageFeatureModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listProviderFeatures',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/features` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findProviderFeature: (
      tenant_id: string,
      provider_id: string,
      feature_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<FeatureModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findProviderFeature',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/features/${encodeURIComponent(String(feature_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileProviderFeature: (
      tenant_id: string,
      provider_id: string,
      feature_id: string,
      body: {
        credentials?: Array<CredentialRequirement>;
        description?: string;
        feature_name?: string;
        options?: any;
        payload_schema?: any;
        requirements?: Array<string>;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileProviderFeature',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/features/${encodeURIComponent(String(feature_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeProviderFeature: (
      tenant_id: string,
      provider_id: string,
      feature_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeProviderFeature',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/features/${encodeURIComponent(String(feature_id))}`,
        undefined,
        finalOptions
      );
    },
    addProviderFeatureRequirement: (
      tenant_id: string,
      provider_id: string,
      feature_id: string,
      body: { requirement?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'addProviderFeatureRequirement',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/features/${encodeURIComponent(String(feature_id))}/requirements`,
        body,
        undefined,
        finalOptions
      );
    },
    removeProviderFeatureRequirement: (
      tenant_id: string,
      provider_id: string,
      feature_id: string,
      query?: { requirement?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeProviderFeatureRequirement',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/features/${encodeURIComponent(String(feature_id))}/requirements` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    renameProvider: (
      tenant_id: string,
      provider_id: string,
      body: { name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'renameProvider',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/name`,
        body,
        undefined,
        finalOptions
      );
    },
    addProviderRequirement: (
      tenant_id: string,
      provider_id: string,
      body: { requirement?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'addProviderRequirement',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/requirements`,
        body,
        undefined,
        finalOptions
      );
    },
    removeProviderRequirement: (
      tenant_id: string,
      provider_id: string,
      query?: { requirement?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeProviderRequirement',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/requirements` +
        (queryString ? '?' + queryString : '');
      return client.del(url, undefined, finalOptions);
    },
    changeProviderStatus: (
      tenant_id: string,
      provider_id: string,
      body: { status?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'changeProviderStatus',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/providers/${encodeURIComponent(String(provider_id))}/status`,
        body,
        undefined,
        finalOptions
      );
    },
    sweepSegmentRetention: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RequestSegmentSweepResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'sweepSegmentRetention',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/retention/sweep`,
        undefined,
        undefined,
        finalOptions
      );
    },
    listRoles: (
      tenant_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageRoleModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRoles',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findRole: (
      tenant_id: string,
      role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<RoleModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findRole',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileRole: (
      tenant_id: string,
      role_id: string,
      body: { description?: string; name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileRole',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeRole: (
      tenant_id: string,
      role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeRole',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}`,
        undefined,
        finalOptions
      );
    },
    listRolePermissions: (
      tenant_id: string,
      role_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageRolePermissionModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listRolePermissions',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/permissions` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    grantRolePermission: (
      tenant_id: string,
      role_id: string,
      permission_id: string,
      body: { permission?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'grantRolePermission',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/permissions/${encodeURIComponent(String(permission_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeRolePermission: (
      tenant_id: string,
      role_id: string,
      permission_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeRolePermission',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/roles/${encodeURIComponent(String(role_id))}/permissions/${encodeURIComponent(String(permission_id))}`,
        undefined,
        finalOptions
      );
    },
    createSecret: (
      tenant_id: string,
      body: {
        name?: string;
        raw?: any;
        secret_id?: string;
        template_id?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'createSecret',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets`,
        body,
        undefined,
        finalOptions
      );
    },
    listSecrets: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<SecretModel>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSecrets',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets`,
        undefined,
        finalOptions
      );
    },
    findSecret: (
      tenant_id: string,
      secret_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SecretModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findSecret',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/${encodeURIComponent(String(secret_id))}`,
        undefined,
        finalOptions
      );
    },
    removeSecret: (
      tenant_id: string,
      secret_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeSecret',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/${encodeURIComponent(String(secret_id))}`,
        undefined,
        finalOptions
      );
    },
    updateSecretName: (
      tenant_id: string,
      secret_id: string,
      body: { name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'updateSecretName',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/${encodeURIComponent(String(secret_id))}/name`,
        body,
        undefined,
        finalOptions
      );
    },
    accessSecret: (
      tenant_id: string,
      secret_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<AccessSecretResult>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'accessSecret',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/${encodeURIComponent(String(secret_id))}/value`,
        undefined,
        finalOptions
      );
    },
    updateSecretValue: (
      tenant_id: string,
      secret_id: string,
      body: { raw?: any },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'updateSecretValue',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/${encodeURIComponent(String(secret_id))}/value`,
        body,
        undefined,
        finalOptions
      );
    },
    listSecretTemplates: (
      tenant_id: string,
      query?: {
        limit?: number;
        next?: string;
        filter?: string;
        active?: boolean;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageSecretTemplateModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSecretTemplates',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/templates` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findSecretTemplate: (
      tenant_id: string,
      template_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SecretTemplateModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findSecretTemplate',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/templates/${encodeURIComponent(String(template_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileSecretTemplate: (
      tenant_id: string,
      template_id: string,
      body: { description?: string; name?: string; schema?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileSecretTemplate',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/templates/${encodeURIComponent(String(template_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeSecretTemplate: (
      tenant_id: string,
      template_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeSecretTemplate',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/templates/${encodeURIComponent(String(template_id))}`,
        undefined,
        finalOptions
      );
    },
    initializeVault: (
      tenant_id: string,
      body: { kek_key_id?: string; kek_version?: number; version?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'initializeVault',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/vault/initialize`,
        body,
        undefined,
        finalOptions
      );
    },
    removeTenantMasterKey: (
      tenant_id: string,
      version: number,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeTenantMasterKey',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/vault/tmk/${encodeURIComponent(String(version))}`,
        undefined,
        finalOptions
      );
    },
    deprecateTenantMasterKey: (
      tenant_id: string,
      body: { version?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deprecateTenantMasterKey',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/vault/tmk/deprecate`,
        body,
        undefined,
        finalOptions
      );
    },
    rewrapTenantMasterKey: (
      tenant_id: string,
      body: { kek_key_id?: string; kek_version?: number; version?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'rewrapTenantMasterKey',
      };
      return client.post(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/secrets/vault/tmk/rewrap`,
        body,
        undefined,
        finalOptions
      );
    },
    listSensitiveContent: (
      tenant_id: string,
      query?: { connector_id?: string; feature_id?: string; limit?: number },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SensitiveContentPage>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSensitiveContent',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/sensitive-content` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    listSiemForwardingDestinations: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<DestinationModel>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSiemForwardingDestinations',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/siem-forwarding/destinations`,
        undefined,
        finalOptions
      );
    },
    createSiemForwardingDestination: (
      tenant_id: string,
      destination_id: string,
      body: {
        connector_id?: string;
        event_filter?: Array<string>;
        feature_name?: string;
      },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'createSiemForwardingDestination',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/siem-forwarding/destinations/${encodeURIComponent(String(destination_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    deleteSiemForwardingDestination: (
      tenant_id: string,
      destination_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'deleteSiemForwardingDestination',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/siem-forwarding/destinations/${encodeURIComponent(String(destination_id))}`,
        undefined,
        finalOptions
      );
    },
    findSiemForwardingDestination: (
      tenant_id: string,
      destination_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<DestinationModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findSiemForwardingDestination',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/siem-forwarding/destinations/${encodeURIComponent(String(destination_id))}`,
        undefined,
        finalOptions
      );
    },
    updateSiemForwardingDestination: (
      tenant_id: string,
      destination_id: string,
      body: { event_filter?: Array<string>; feature_name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'updateSiemForwardingDestination',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/siem-forwarding/destinations/${encodeURIComponent(String(destination_id))}/settings`,
        body,
        undefined,
        finalOptions
      );
    },
    changeSiemForwardingDestinationStatus: (
      tenant_id: string,
      destination_id: string,
      body: { status?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId:
          options?.operationId ?? 'changeSiemForwardingDestinationStatus',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/siem-forwarding/destinations/${encodeURIComponent(String(destination_id))}/status`,
        body,
        undefined,
        finalOptions
      );
    },
    listSiemForwardingEventKinds: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<Array<SiemEventKind>>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listSiemForwardingEventKinds',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/siem-forwarding/event-kinds`,
        undefined,
        finalOptions
      );
    },
    getSiemForwardingSettings: (
      tenant_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<SiemForwardingSettings>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'getSiemForwardingSettings',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/siem-forwarding/settings`,
        undefined,
        finalOptions
      );
    },
    changeTenantSlug: (
      tenant_id: string,
      body: { slug?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'changeTenantSlug',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/slug`,
        body,
        undefined,
        finalOptions
      );
    },
    listTeams: (
      tenant_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageTeamModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listTeams',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    findTeam: (
      tenant_id: string,
      team_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<TeamModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findTeam',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}`,
        undefined,
        finalOptions
      );
    },
    reconcileTeam: (
      tenant_id: string,
      team_id: string,
      body: { description?: string; name?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'reconcileTeam',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}`,
        body,
        undefined,
        finalOptions
      );
    },
    removeTeam: (
      tenant_id: string,
      team_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeTeam',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}`,
        undefined,
        finalOptions
      );
    },
    listTeamMembers: (
      tenant_id: string,
      team_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageTeamMemberModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listTeamMembers',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}/members` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    assignTeamMember: (
      tenant_id: string,
      team_id: string,
      user_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'assignTeamMember',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}/members/${encodeURIComponent(String(user_id))}`,
        undefined,
        undefined,
        finalOptions
      );
    },
    findTeamMember: (
      tenant_id: string,
      team_id: string,
      user_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<TeamMemberModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findTeamMember',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}/members/${encodeURIComponent(String(user_id))}`,
        undefined,
        finalOptions
      );
    },
    removeTeamMember: (
      tenant_id: string,
      team_id: string,
      user_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeTeamMember',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}/members/${encodeURIComponent(String(user_id))}`,
        undefined,
        finalOptions
      );
    },
    listTeamRoles: (
      tenant_id: string,
      team_id: string,
      query?: { limit?: number; next?: string; filter?: string },
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<PageTeamRoleModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'listTeamRoles',
      };
      const queryString = query ? buildQueryParams(query) : '';
      const url =
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}/roles` +
        (queryString ? '?' + queryString : '');
      return client.get(url, undefined, finalOptions);
    },
    assignTeamRole: (
      tenant_id: string,
      team_id: string,
      role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'assignTeamRole',
      };
      return client.put(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}/roles/${encodeURIComponent(String(role_id))}`,
        undefined,
        undefined,
        finalOptions
      );
    },
    findTeamRole: (
      tenant_id: string,
      team_id: string,
      role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<TeamRoleModel>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'findTeamRole',
      };
      return client.get(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}/roles/${encodeURIComponent(String(role_id))}`,
        undefined,
        finalOptions
      );
    },
    removeTeamRole: (
      tenant_id: string,
      team_id: string,
      role_id: string,
      options?: { signal?: AbortSignal; timeout?: number; operationId?: string }
    ): Promise<FetchResponse<boolean>> => {
      const finalOptions = {
        ...options,
        operationId: options?.operationId ?? 'removeTeamRole',
      };
      return client.del(
        `/api/v1/tenants/${encodeURIComponent(String(tenant_id))}/teams/${encodeURIComponent(String(team_id))}/roles/${encodeURIComponent(String(role_id))}`,
        undefined,
        finalOptions
      );
    },
  };
}

/** AccessSecretResult schema */
export interface AccessSecretResult {
  value?: any;
}

/** ActionInputPreview schema */
export interface ActionInputPreview {
  contract?: ConnectorFeatureContract_aa1d27dd;
  envelope?: Record<string, string>;
  idempotency_key?: string;
  resolved_input?: Record<string, string>;
  template_context?: AutomationTemplateContext;
}

/** AgentDownloadArtifact schema */
export interface AgentDownloadArtifact {
  arch?: string;
  binary?: string;
  bundle_url?: string;
  checksum_algorithm?: string;
  checksum_file?: string;
  checksum_path?: string;
  checksum_url?: string;
  filename?: string;
  os?: string;
  path?: string;
  url?: string;
}

/** AgentDownloadsResponse schema */
export interface AgentDownloadsResponse {
  agent?: Array<AgentDownloadArtifact>;
  agentd?: Array<AgentDownloadArtifact>;
}

/** AgentFleet schema */
export interface AgentFleet {
  agents?: Array<AgentPresence>;
  /** When this view was taken, server-stamped. */
  observed_at?: string;
  /** False while the first inventory for this tenant is still outstanding, in which case an empty agent list means unknown rather than none. */
  settled?: boolean;
}

/** AgentPresence schema */
export interface AgentPresence {
  /** Work this agent can execute; an invocation's requirements must be a subset of this. */
  capabilities?: Array<string>;
  /** Agent identity. Matches the claiming executor recorded on invocations it runs. */
  client_id?: string;
  /** Operator-assigned labels for targeting and filtering. Mesh attaches no behavior to them. */
  groups?: Array<string>;
  /** Host the agent runs on, as the agent reports it. */
  hostname?: string;
  /** Server-stamped time of the agent's last presence renewal. */
  last_seen_at?: string;
  /** When this agent is swept from the roll call if it stops renewing. */
  stale_after?: string;
  /** Running agent build. */
  version?: string;
}

/** AggregationConfig schema */
export interface AggregationConfig {
  function?: string;
  group_by?: Array<string>;
  threshold?: number;
  threshold_operator?: string;
  value_field?: string;
  window?: number;
  window_kind?: string;
  window_slide?: number;
}

/** ApplyBatchesObservation schema */
export interface ApplyBatchesObservation {
  completed?: number;
  total?: number;
}

/** AutomationAction schema */
export interface AutomationAction {
  connector_ref?: string;
  feature_ref?: string;
  hydrations?: Array<ModelHydration>;
  idempotency_template?: string;
  input_mapping?: AutomationInputMapping;
  payload_template?: any;
}

/** AutomationFieldBinding schema */
export interface AutomationFieldBinding {
  from?: string;
  to?: string;
}

/** AutomationInputMapping schema */
export interface AutomationInputMapping {
  fields?: Array<AutomationFieldBinding>;
}

/** AutomationRule schema */
export interface AutomationRule {
  action?: AutomationAction;
  created_at?: string;
  dedup?: DedupPolicy;
  description?: string;
  enabled?: boolean;
  filter_group?: ConditionGroup;
  filters?: Array<Filter>;
  name?: string;
  priority?: number;
  rate_limit?: RateLimitPolicy_02fbba5e;
  retry?: RetryPolicy;
  rule_id?: string;
  tenant_id?: string;
  trigger?: Trigger;
  updated_at?: string;
  version?: number;
}

/** AutomationTemplateContext schema */
export interface AutomationTemplateContext {
  CorrelationID?: string;
  Discriminator?: string;
  EventID?: string;
  EventSpace?: string;
  Metadata?: Record<string, string>;
  Models?: Record<string, string>;
  OccurredAt?: string;
  Payload?: Record<string, string>;
  Source?: string;
  TenantID?: string;
}

/** CampaignAccount schema */
export interface CampaignAccount {
  account_id: string;
  account_ref: string;
}

/** CatalogApplyObservation schema */
export interface CatalogApplyObservation {
  /** Deletes that removed an entity from the read model */
  deletes_applied?: number;
  /** Delete candidates skipped because the entity was not present in the read model */
  deletes_skipped_absent?: number;
  /** Upserts that produced a non-empty patch at apply time */
  upserts_applied?: number;
  /** Upsert candidates that applied as no-op (empty patch vs read model) */
  upserts_noop?: number;
}

/** ClientModel schema */
export interface ClientModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  capabilities?: Array<string>;
  client_id?: string;
  created_by?: string;
  description?: string;
  enabled?: boolean;
  /** When the client credential expires */
  expires_at?: string;
  is_revoked?: boolean;
  name?: string;
  /** When the client was revoked */
  revoked_at?: string;
  revoked_by?: string;
  role_ids?: Array<string>;
  tenant_id?: string;
}

/** CollectionObservation schema */
export interface CollectionObservation {
  entities_collected?: number;
  space_names?: Array<string>;
}

/** Condition schema */
export interface Condition {
  parameters?: Record<string, string>;
  type?: string;
}

/** ConditionGroup schema */
export interface ConditionGroup {
  filters?: Array<Filter>;
  groups?: Array<ConditionGroup>;
  logic?: string;
}

/** ConnectorFeatureContract schema */
export interface ConnectorFeatureContract {
  connector?: ConnectorModel;
  feature?: ConnectorFeatureModel;
  input_schema?: any;
  provider_feature?: FeatureModel;
}

/** ConnectorFeatureContract_aa1d27dd schema */
export interface ConnectorFeatureContract_aa1d27dd {
  connector?: ConnectorModel;
  feature?: ConnectorFeatureModel;
  input_schema?: any;
  provider_feature?: FeatureModel;
}

/** ConnectorFeatureInvocationSequence schema */
export interface ConnectorFeatureInvocationSequence {
  in_flight?: boolean;
  sequence_num?: number;
}

/** ConnectorFeatureModel schema */
export interface ConnectorFeatureModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  /** Canonical taxonomy verb the action feature declares in its manifest (denormalized from the provider feature; empty for collectors and undeclared actions). */
  action_type?: string;
  client_ids?: Array<string>;
  connector_id?: string;
  enabled?: boolean;
  feature_id?: string;
  feature_name?: string;
  is_removed?: boolean;
  options?: any;
  payload_schema?: any;
  provider_id?: string;
  provider_name?: string;
  required_sites?: Array<string>;
  requirements?: Array<string>;
  schedulable?: boolean;
  /** Bound secrets keyed by credential slot name. */
  secret_ids?: Record<string, string>;
  spaces?: Array<string>;
  tenant_id?: string;
  /** Denormalized provider feature category. */
  type?: 'collector' | 'action';
}

/** ConnectorInvocationStatusModel schema */
export interface ConnectorInvocationStatusModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  /** Executor that claimed and ran this invocation, as its client_id (also its presence WorkerID, so fleet views join on it directly). Placement is a claim race, so in a hybrid cloud/on-prem fleet this is the only record of where the run executed. Null for a run that never started, or one claimed by a principal with no resolvable identity. */
  claimed_by?: string;
  /** True once metrics include reconciliation.collection_completed_at (connector finished producing payloads and Mesh began catalog reconciliation), or when the run completes successfully. */
  collection_complete?: boolean;
  completed_at?: string;
  connector_id?: string;
  failed_at?: string;
  feature_id?: string;
  last_completed_at?: string;
  message?: string;
  metrics?: InvocationObservationMetrics;
  processed_count?: number;
  requested_at?: string;
  run_sequence?: number;
  started_at?: string;
  /** Projected lifecycle state for this invocation run. queued means the run is scheduled but not yet requested. waiting means Mesh is blocked on a dependency such as catalog KV/RSOS readiness. running means the connector subprocess is producing payloads. updating means Mesh is reconciling staged catalog data (RSOS/planning and/or HTTP catalog apply) before the run becomes terminal. */
  status?:
    | 'queued'
    | 'requested'
    | 'running'
    | 'reconciling'
    | 'waiting'
    | 'updating'
    | 'completed'
    | 'failed'
    | 'stopped';
  stopped_at?: string;
  tenant_id?: string;
}

/** ConnectorModel schema */
export interface ConnectorModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  connector_id?: string;
  enabled?: boolean;
  is_internal?: boolean;
  is_revoked?: boolean;
  is_system?: boolean;
  name?: string;
  provider_id?: string;
  provider_logo_dark_uri?: string;
  provider_logo_light_uri?: string;
  provider_name?: string;
  required_sites?: Array<string>;
  requirements?: Array<string>;
  revoked_at?: string;
  revoked_by?: string;
  sync?: ConnectorSyncModel;
  tenant_id?: string;
}

/** ConnectorSyncModel schema */
export interface ConnectorSyncModel {
  /** When this connector's data last landed (newest completed collector run). Omitted when it has never completed one; never_synced reports that case. */
  last_completed_at?: string;
  /** Message from the most recent failed collector run, as the connector reported it. Free text, not a classified error code. */
  last_error?: string;
  /** When the most recent collector run failed. Present only while that failure is still the connector's latest run. */
  last_failure_at?: string;
  /** Status of the most recent collector run, whatever its outcome. */
  last_status?:
    | 'queued'
    | 'requested'
    | 'running'
    | 'reconciling'
    | 'waiting'
    | 'updating'
    | 'completed'
    | 'failed'
    | 'stopped';
  /** True when no collector run has ever completed for this connector. */
  never_synced?: boolean;
  /** True when the last completed collector run is older than the stale_after threshold. */
  stale?: boolean;
}

/** CreateClientResult schema */
export interface CreateClientResult {
  client_id?: string;
  client_secret?: string;
}

/** CreateEnrollmentSecretResult schema */
export interface CreateEnrollmentSecretResult {
  credential_id?: string;
  name?: string;
  secret?: string;
  tenant_id?: string;
}

/** CredentialRequirement schema */
export interface CredentialRequirement {
  /** Stable credential slot key used when binding a secret */
  name?: string;
  /** Resolved secret template identifier for this slot */
  template_id?: string;
  /** Secret template shape the bound secret must conform to */
  template_name?: string;
}

/** CredentialRequirement_c5dfff01 schema */
export interface CredentialRequirement_c5dfff01 {
  name?: string;
  template_name?: string;
}

/** CredentialRequirement_fff6ab19 schema */
export interface CredentialRequirement_fff6ab19 {
  name?: string;
  template_name?: string;
}

/** DLQEntry schema */
export interface DLQEntry {
  attempts?: number;
  connector_ref?: string;
  entry_id?: string;
  feature_ref?: string;
  first_attempted_at?: string;
  idempotency_key?: string;
  input?: Record<string, string>;
  last_attempted_at?: string;
  last_error?: string;
  original_event_snapshot?: TriggerEvent;
  rule_id?: string;
  rule_snapshot?: AutomationRule;
  rule_version?: number;
  tenant_id?: string;
  trigger_discriminator?: string;
  trigger_event_id?: string;
}

/** DLQEntry_f60d2c45 schema */
export interface DLQEntry_f60d2c45 {
  attempts?: number;
  connector_ref?: string;
  entry_id?: string;
  feature_ref?: string;
  first_attempted_at?: string;
  idempotency_key?: string;
  input?: Record<string, string>;
  last_attempted_at?: string;
  last_error?: string;
  original_event_snapshot?: TriggerEvent;
  rule_id?: string;
  rule_snapshot?: AutomationRule;
  rule_version?: number;
  tenant_id?: string;
  trigger_discriminator?: string;
  trigger_event_id?: string;
}

/** DLQRetryResult schema */
export interface DLQRetryResult {
  entry?: DLQEntry_f60d2c45;
  execution?: RuleExecutionResult;
  resolved?: boolean;
}

/** DedupPolicy schema */
export interface DedupPolicy {
  key_template?: string;
  window?: number;
}

/** DestinationModel schema */
export interface DestinationModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  connector_id?: string;
  created_at?: string;
  destination_id?: string;
  enabled?: boolean;
  /** Activity event discriminators to forward; empty forwards all events. */
  event_filter?: Array<string>;
  /** Provider feature name to invoke (a SIEM emit-event-batch action). */
  feature_name?: string;
  /** When the forwarder last delivered a batch to this destination. */
  last_delivery_at?: string;
  /** Most recent delivery error reported by the forwarder; cleared on success. */
  last_error?: string;
  tenant_id?: string;
  updated_at?: string;
}

/** EmailAddress schema */
export interface EmailAddress {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  email?: string;
  user_id?: string;
  verified?: boolean;
}

/** EnrollmentCredentialModel schema */
export interface EnrollmentCredentialModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  /** Capabilities granted to every agent enrolled through this credential */
  capabilities?: Array<string>;
  created_by?: string;
  credential_id?: string;
  /** When the credential stops accepting enrollments; absent means valid until revoked */
  expires_at?: string;
  groups?: Array<string>;
  is_revoked?: boolean;
  /** join_code enrolls exactly one agent; shared_secret enrolls many until revoked */
  kind?: 'join_code' | 'shared_secret';
  /** When the credential last enrolled an agent */
  last_used_at?: string;
  name?: string;
  revoked_at?: string;
  revoked_by?: string;
  role_ids?: Array<string>;
  tenant_id?: string;
  /** How many agents have enrolled through this credential */
  use_count?: number;
}

/** EnrollmentCredentialUseModel schema */
export interface EnrollmentCredentialUseModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  /** The agent identity this enrollment created */
  client_id?: string;
  credential_id?: string;
  enrolled_at?: string;
  /** The enrolling machine as it reported itself */
  hostname?: string;
  tenant_id?: string;
}

/** EvaluationRule schema */
export interface EvaluationRule {
  aggregation?: AggregationConfig;
  condition?: Condition;
  condition_group?: ConditionGroup;
  created_at?: string;
  dedup?: DedupPolicy;
  description?: string;
  enabled?: boolean;
  filters?: Array<Filter>;
  name?: string;
  outputs?: Array<EventOutput>;
  pattern?: PatternConfig;
  priority?: number;
  published_version?: number;
  rate_limit?: RateLimitPolicy;
  rule_id?: string;
  snapshot_signature?: string;
  source_sql?: string;
  status?: string;
  tenant_id?: string;
  trigger?: Trigger;
  triggers?: Array<EvaluatorTrigger>;
  type?: string;
  updated_at?: string;
  version?: number;
}

/** EvaluationRuleUpdateInput schema */
export interface EvaluationRuleUpdateInput {
  aggregation?: AggregationConfig;
  condition?: Condition;
  condition_group?: ConditionGroup;
  description?: string;
  enabled?: boolean;
  name?: string;
  outputs?: Array<EventOutput>;
  pattern?: PatternConfig;
  sql?: string;
  triggers?: Array<EvaluatorTrigger>;
  type?: string;
}

/** EvaluatorTrigger schema */
export interface EvaluatorTrigger {
  event_space?: string;
  event_type?: string;
  filters?: Array<Filter>;
  priority?: number;
  window?: TriggerWindow;
}

/** EventOutput schema */
export interface EventOutput {
  event_space?: string;
  event_type?: string;
  payload_template?: Record<string, string>;
  stream_segment?: string;
}

/** EventProcessingResult schema */
export interface EventProcessingResult {
  emitted_signals?: number;
  event_data?: Record<string, string>;
  event_id?: string;
  failed_rules?: number;
  matched_rules?: number;
  results?: Array<RuleExecutionResult_7e490a83>;
  source_event_type?: string;
  tenant_id?: string;
  timestamp?: string;
  triggered_rules?: number;
}

/** ExecutionRecord schema */
export interface ExecutionRecord {
  action_completed_at?: string;
  attempt?: number;
  completed_at?: string;
  connector_ref?: string;
  duration_nanos?: number;
  error?: string;
  execution_id?: string;
  feature_ref?: string;
  idempotency_key?: string;
  invocation_run_sequence?: number;
  rule_id?: string;
  rule_version?: number;
  started_at?: string;
  status?: string;
  tenant_id?: string;
  trigger_discriminator?: string;
  trigger_event_id?: string;
}

/** Feature schema */
export interface Feature {
  action_type?: string;
  credentials?: Array<CredentialRequirement_c5dfff01>;
  description?: string;
  display_name?: string;
  feature_id?: string;
  name?: string;
  options_schema?: any;
  payload_schema?: any;
  requirements?: Array<string>;
  resume_behavior?: string;
  schedulable?: boolean;
  secret_template_name?: string;
  spaces?: Array<string>;
  type?: string;
}

/** FeatureModel schema */
export interface FeatureModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  /** Canonical taxonomy verb the action feature declares in its manifest (e.g. account.disable, group.member.remove, universal.ticket.open). Empty for collectors and for actions that have not declared one — undeclared features are invisible to taxonomy-driven consumers. */
  action_type?: string;
  credentials?: Array<CredentialRequirement>;
  description?: string;
  feature_id?: string;
  manifest_version?: string;
  name?: string;
  options?: any;
  payload_schema?: any;
  provider_id?: string;
  requirements?: Array<string>;
  schedulable?: boolean;
  /** Catalog spaces this collector emits to, from the manifest. Empty for actions. Also empty until the feature is imported at a manifest version carrying spaces, since re-importing an already-installed version is a no-op. */
  spaces?: Array<string>;
  /** Lifecycle: active (offered) or retired. Omitted or empty JSON is still accepted and treated as active for catalog matching. */
  status?: 'active' | 'retired';
  tenant_id?: string;
  /** Feature kind driving behavior/routing: collector ingests, action operates. Distinct from the provider's catalog category. */
  type?: 'collector' | 'action';
}

/** Filter schema */
export interface Filter {
  field?: string;
  operator?: string;
  value?: string;
}

/** InvitationModel schema */
export interface InvitationModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  accepted_at?: string;
  declined_at?: string;
  email?: string;
  invitation_id?: string;
  sent_at?: string;
  status?: string;
  tenant_id?: string;
  user_id?: string;
}

/** InvitationResult schema */
export interface InvitationResult {
  invitation_id?: string;
  tenant_id?: string;
  user_id?: string;
}

/** InvocationLifecycleObservation schema */
export interface InvocationLifecycleObservation {
  /** collecting | processing | updating */
  step?: string;
}

/** InvocationObservationMetrics schema */
export interface InvocationObservationMetrics {
  collection?: CollectionObservation;
  lifecycle?: InvocationLifecycleObservation;
  reconciliation?: ReconciliationObservation;
}

/** IssueEnrollmentJoinCodeResult schema */
export interface IssueEnrollmentJoinCodeResult {
  code?: string;
  credential_id?: string;
  expires_at?: string;
  tenant_id?: string;
}

/** Logo schema */
export interface Logo {
  dark?: string;
  light?: string;
}

/** Manifest schema */
export interface Manifest {
  /** Catalog category declared by the connector manifest. Always reflects the catalog, unlike ProviderModel.category, which only advances when a provider is imported at a new version. */
  category?: string;
  description?: string;
  display_name?: string;
  features?: Record<string, Feature>;
  name?: string;
  provider_id?: string;
  version?: string;
}

/** ManifestModel schema */
export interface ManifestModel {
  installed_version?: string;
  latest_version?: string;
  logo?: Logo;
  manifest?: Manifest;
  upgrade_available?: boolean;
}

/** MatchedEvent schema */
export interface MatchedEvent {
  alias?: string;
  event_data?: Record<string, string>;
  event_id?: string;
  event_type?: string;
  matched_at?: string;
}

/** MemberModel schema */
export interface MemberModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  added_at?: string;
  profile?: UserProfile;
  status?: string;
  tenant_id?: string;
  user_id?: string;
}

/** MemberResult schema */
export interface MemberResult {
  tenant_id?: string;
  user_id?: string;
}

/** ModelHydration schema */
export interface ModelHydration {
  as?: string;
  entity_id_from?: string;
  relationship_type?: string;
}

/** PageConnectorFeatureModel schema */
export interface PageConnectorFeatureModel {
  models?: Array<ConnectorFeatureModel>;
  next?: string;
  prev?: string;
}

/** PageConnectorInvocationStatusModel schema */
export interface PageConnectorInvocationStatusModel {
  models?: Array<ConnectorInvocationStatusModel>;
  next?: string;
  prev?: string;
}

/** PageConnectorModel schema */
export interface PageConnectorModel {
  models?: Array<ConnectorModel>;
  next?: string;
  prev?: string;
}

/** PageFeatureModel schema */
export interface PageFeatureModel {
  models?: Array<FeatureModel>;
  next?: string;
  prev?: string;
}

/** PageInvitationModel schema */
export interface PageInvitationModel {
  models?: Array<InvitationModel>;
  next?: string;
  prev?: string;
}

/** PageManifestModel schema */
export interface PageManifestModel {
  models?: Array<ManifestModel>;
  next?: string;
  prev?: string;
}

/** PageMemberModel schema */
export interface PageMemberModel {
  models?: Array<MemberModel>;
  next?: string;
  prev?: string;
}

/** PageProviderModel schema */
export interface PageProviderModel {
  models?: Array<ProviderModel>;
  next?: string;
  prev?: string;
}

/** PageRoleModel schema */
export interface PageRoleModel {
  models?: Array<RoleModel>;
  next?: string;
  prev?: string;
}

/** PageRolePermissionModel schema */
export interface PageRolePermissionModel {
  models?: Array<RolePermissionModel>;
  next?: string;
  prev?: string;
}

/** PageSecretTemplateModel schema */
export interface PageSecretTemplateModel {
  models?: Array<SecretTemplateModel>;
  next?: string;
  prev?: string;
}

/** PageTeamMemberModel schema */
export interface PageTeamMemberModel {
  models?: Array<TeamMemberModel>;
  next?: string;
  prev?: string;
}

/** PageTeamModel schema */
export interface PageTeamModel {
  models?: Array<TeamModel>;
  next?: string;
  prev?: string;
}

/** PageTeamRoleModel schema */
export interface PageTeamRoleModel {
  models?: Array<TeamRoleModel>;
  next?: string;
  prev?: string;
}

/** PageTenantModel schema */
export interface PageTenantModel {
  models?: Array<TenantModel>;
  next?: string;
  prev?: string;
}

/** PatternConfig schema */
export interface PatternConfig {
  correlation_fields?: Array<string>;
  max_duration?: number;
  sequence?: Array<PatternStep>;
}

/** PatternStateView schema */
export interface PatternStateView {
  completed?: boolean;
  completed_at?: string;
  correlation_id?: string;
  current_step?: number;
  expected_alias?: string;
  expected_next?: string;
  expires_at?: string;
  last_event_at?: string;
  last_event_id?: string;
  matched_events?: Array<MatchedEvent>;
  max_duration?: number;
  rule_id?: string;
  rule_version?: number;
  sequence?: Array<PatternStep>;
  snapshot_signature?: string;
  tenant_id?: string;
  time_remaining_ns?: number;
  total_steps?: number;
}

/** PatternStep schema */
export interface PatternStep {
  alias?: string;
  event_type?: string;
  filters?: Array<Filter>;
  within?: number;
}

/** PreparedEvent schema */
export interface PreparedEvent {
  event_space?: string;
  event_type?: string;
  occurred_at?: string;
  payload?: Record<string, string>;
  rule_id?: string;
  rule_version?: number;
  snapshot_signature?: string;
  source_event_id?: string;
  source_event_type?: string;
  stream_segment?: string;
  tenant_id?: string;
}

/** PreviewActionInput schema */
export interface PreviewActionInput {
  action: AutomationAction;
  event: TriggerEvent;
  tenant_id: string;
}

/** ProblemDetails schema */
export interface ProblemDetails {
  detail?: string;
  instance?: string;
  status?: number;
  title?: string;
  type?: string;
}

/** PromoteSensitiveContentRequest schema */
export interface PromoteSensitiveContentRequest {
  /** Name of the secret to create (required, at most 128 characters) */
  name?: string;
}

/** ProviderModel schema */
export interface ProviderModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  /** Catalog category from the provider manifest (not an enum; new mesh-sdk categories need no mesh-core release). Empty until the provider is imported at a manifest version carrying one, since re-importing an installed version is a no-op. */
  category?: string;
  description?: string;
  enabled?: boolean;
  is_internal?: boolean;
  is_system?: boolean;
  logo_dark_uri?: string;
  logo_light_uri?: string;
  manifest_source_ref?: string;
  manifest_version?: string;
  name?: string;
  provider_id?: string;
  requirements?: Array<string>;
  status?: string;
  tenant_id?: string;
}

/** RSOSPlanObservation schema */
export interface RSOSPlanObservation {
  /** Deletes the RSOS plan proposes for owned spaces (candidates only; use catalog_apply.deletes_applied for removals from the read model) */
  delete_candidates?: number;
  /** Entities already matching remote in this space (skipped in plan) */
  unchanged_vs_remote?: number;
  /** Upserts the RSOS plan would touch vs remote tree (candidates only) */
  upsert_candidates?: number;
}

/** RateLimitPolicy schema */
export interface RateLimitPolicy {
  max_signals?: number;
  window?: number;
}

/** RateLimitPolicy_02fbba5e schema */
export interface RateLimitPolicy_02fbba5e {
  max_actions?: number;
  window?: number;
}

/** RateLimitPolicy_334c3503 schema */
export interface RateLimitPolicy_334c3503 {
  max_actions?: number;
  window?: number;
}

/** RateLimitPolicy_b4b5191d schema */
export interface RateLimitPolicy_b4b5191d {
  max_actions?: number;
  window?: number;
}

/** ReconcileConnectorFeature schema */
export interface ReconcileConnectorFeature {
  connector_id: string;
  enabled?: boolean;
  feature_id: string;
  options?: any;
  required_sites?: Array<string>;
  requirements?: Array<string>;
  secret_ids?: any;
  tenant_id: string;
}

/** ReconciliationObservation schema */
export interface ReconciliationObservation {
  apply_batches?: ApplyBatchesObservation;
  catalog_apply?: CatalogApplyObservation;
  /** RFC3339 time when the catalog apply phase begins (before the first HTTP /apply); not the first batch completion */
  catalog_apply_started_at?: string;
  /** RFC3339 when the connector finished collection and Mesh stamped catalog reconciliation metrics (beginning of the reconciliation block on observations) */
  collection_completed_at?: string;
  /** RFC3339 time when RSOS planning finished for all catalog spaces (before catalog apply) */
  planning_completed_at?: string;
  planning_space?: string;
  /** RFC3339 time when catalog reconciliation finished (staging apply + local cleanup); not set until the run leaves updating */
  reconciliation_completed_at?: string;
  rsos_plan?: RSOSPlanObservation;
  spaces?: SpacesProgressObservation;
  staged_total?: number;
  updating_phase?: string;
  /** RFC3339 time when the run entered updating after collection finished */
  updating_started_at?: string;
  waiting?: WaitingObservation;
}

/** RequestConnectorInvocationResult schema */
export interface RequestConnectorInvocationResult {
  connector_id?: string;
  feature_id?: string;
  run_sequence?: number;
  tenant_id?: string;
}

/** RequestEventIndexBackfillResult schema */
export interface RequestEventIndexBackfillResult {
  attempt?: number;
  cursor?: string;
  reason?: string;
  tenant_id?: string;
}

/** RequestSegmentSweepResult schema */
export interface RequestSegmentSweepResult {
  attempt?: number;
  reason?: string;
  tenant_id?: string;
  window?: string;
}

/** Resolution schema */
export interface Resolution {
  executable_name?: string;
  feature?: Feature;
  manifest_source?: string;
  manifest_version?: string;
}

/** RetryPolicy schema */
export interface RetryPolicy {
  backoff?: string;
  max_attempts?: number;
}

/** RoleModel schema */
export interface RoleModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  active?: boolean;
  description?: string;
  is_system?: boolean;
  name?: string;
  reconciled_at?: string;
  role_id?: string;
  tenant_id?: string;
}

/** RoleNode schema */
export interface RoleNode {
  id?: string;
  name?: string;
}

/** RolePermissionModel schema */
export interface RolePermissionModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  granted?: boolean;
  permission?: string;
  role_id?: string;
  role_permission_id?: string;
  tenant_id?: string;
}

/** RotateClientSecretResult schema */
export interface RotateClientSecretResult {
  client_secret?: string;
}

/** RotateEnrollmentSecretResult schema */
export interface RotateEnrollmentSecretResult {
  credential_id?: string;
  secret?: string;
  tenant_id?: string;
}

/** RuleExecutionResult schema */
export interface RuleExecutionResult {
  attempt?: number;
  completed_at?: string;
  connector_ref?: string;
  duration?: number;
  error_msg?: string;
  executed_at?: string;
  feature_ref?: string;
  idempotency_key?: string;
  input?: Record<string, string>;
  invocation_run_sequence?: number;
  matched?: boolean;
  output?: Record<string, string>;
  rule_id?: string;
  rule_version?: number;
  status?: string;
  trigger_discriminator?: string;
  trigger_event_id?: string;
  triggered?: boolean;
}

/** RuleExecutionResult_7e490a83 schema */
export interface RuleExecutionResult_7e490a83 {
  completed_at?: string;
  duration?: number;
  error_msg?: string;
  event_id?: string;
  executed_at?: string;
  matched?: boolean;
  outputs?: Array<EventOutput>;
  published_version?: number;
  rule_id?: string;
  rule_version?: number;
  signals?: Array<PreparedEvent>;
  snapshot_signature?: string;
  source_event_type?: string;
  suppressed_reason?: string;
  triggered?: boolean;
}

/** RuleUpdateInput schema */
export interface RuleUpdateInput {
  action?: AutomationAction;
  dedup?: DedupPolicy;
  description?: string;
  enabled?: boolean;
  filter_group?: ConditionGroup;
  filters?: Array<Filter>;
  name?: string;
  priority?: number;
  rate_limit?: RateLimitPolicy_b4b5191d;
  retry?: RetryPolicy;
  trigger?: Trigger;
}

/** RunResultPayload schema */
export interface RunResultPayload {
  message?: string;
  processed_count?: number;
  refusal_code?: string;
  result?: any;
  resume_cursor?: any;
  sealed_result?: SealedResult;
}

/** RunningConnectorInvocationModel schema */
export interface RunningConnectorInvocationModel {
  /** Executor currently running this invocation, as its client_id (also its presence WorkerID). Null when the claiming principal exposed no resolvable identity, or the start has not been observed yet. */
  claimed_by?: string;
  connector_id?: string;
  feature_id?: string;
  run_sequence?: number;
  /** When the run started, from the started event's own timestamp. Empty for an entry observed only through a progress report. */
  started_at?: string;
}

/** RunningConnectorInvocationsPage schema */
export interface RunningConnectorInvocationsPage {
  invocations?: Array<RunningConnectorInvocationModel>;
  /** True when more in-flight runs exist beyond the returned page; the caller saw only the oldest N. */
  truncated?: boolean;
}

/** SSOUserIdentity schema */
export interface SSOUserIdentity {
  provider?: string;
  subject?: string;
  user_id?: string;
}

/** SealedResult schema */
export interface SealedResult {
  ciphertext?: string;
  expires_at?: string;
  key_id?: string;
  template_name?: string;
}

/** SecretModel schema */
export interface SecretModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  active_alg_version?: number;
  active_tmk_version?: number;
  alg_version?: number;
  created_at?: string;
  is_outdated?: boolean;
  name?: string;
  rotated_at?: string;
  secret_id?: string;
  status?: string;
  template_id?: string;
  tmk_version?: number;
}

/** SecretTemplateModel schema */
export interface SecretTemplateModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  description?: string;
  name?: string;
  /** True for a template seeded from mesh-sdk. Its content is owned by the SDK, so it cannot be edited. */
  read_only?: boolean;
  schema?: any;
  status?: string;
  template_id?: string;
  tenant_id?: string;
}

/** SensitiveContentModel schema */
export interface SensitiveContentModel {
  /** When mesh-core received the result */
  collected_at?: string;
  /** Connector whose action run returned the value */
  connector_id?: string;
  /** Name of the connector; empty when the caller lacks connectors:read or it no longer resolves */
  connector_name?: string;
  /** When the row expires; from then on no read returns it */
  expires_at?: string;
  /** Provider feature whose action run returned the value */
  feature_id?: string;
  /** Name of the provider feature; empty when the caller lacks connectors:read or providers:read, or it no longer resolves */
  feature_name?: string;
  /** ID (distinct_id) of the row */
  id?: string;
  /** Run sequence of the action run that returned the value */
  run_sequence?: number;
  /** Size of the stored ciphertext in bytes */
  size_bytes?: number;
  /** The value did not match the template the connector named and is stored free-form */
  template_mismatch?: boolean;
  /** Secret template the value matches; empty when free-form */
  template_name?: string;
}

/** SensitiveContentModel_30c3e8cf schema */
export interface SensitiveContentModel_30c3e8cf {
  /** When mesh-core received the result */
  collected_at?: string;
  /** Connector whose action run returned the value */
  connector_id?: string;
  /** Name of the connector; empty when the caller lacks connectors:read or it no longer resolves */
  connector_name?: string;
  /** When the row expires; from then on no read returns it */
  expires_at?: string;
  /** Provider feature whose action run returned the value */
  feature_id?: string;
  /** Name of the provider feature; empty when the caller lacks connectors:read or providers:read, or it no longer resolves */
  feature_name?: string;
  /** ID (distinct_id) of the row */
  id?: string;
  /** Run sequence of the action run that returned the value */
  run_sequence?: number;
  /** Size of the stored ciphertext in bytes */
  size_bytes?: number;
  /** The value did not match the template the connector named and is stored free-form */
  template_mismatch?: boolean;
  /** Secret template the value matches; empty when free-form */
  template_name?: string;
}

/** SensitiveContentPage schema */
export interface SensitiveContentPage {
  models?: Array<SensitiveContentModel_30c3e8cf>;
  /** True when more live rows match than the limit returned */
  truncated?: boolean;
}

/** SensitiveContentPromoted schema */
export interface SensitiveContentPromoted {
  /** ID of the secret, uuid.NewSHA1(sensitive_content_id, "promoted") */
  secret_id?: string;
}

/** SensitiveContentValue schema */
export interface SensitiveContentValue {
  /** The value the action returned, as JSON */
  value?: any;
}

/** SiemEventKind schema */
export interface SiemEventKind {
  /** Polymorphic event discriminator, e.g. mesh://catalog/v1/events/login_succeeded */
  discriminator?: string;
  /** Human-readable name derived from the discriminator */
  name?: string;
  /** Catalog space the event routes to (e.g. activity) */
  space?: string;
}

/** SiemForwardingSettings schema */
export interface SiemForwardingSettings {
  /** Whether SIEM forwarding is enabled for this tenant via the siem_forwarding_enabled feature flag */
  enabled?: boolean;
}

/** SlugModel schema */
export interface SlugModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  slug?: string;
  tenant_id?: string;
}

/** SpacesProgressObservation schema */
export interface SpacesProgressObservation {
  completed?: number;
  total?: number;
}

/** TeamMemberModel schema */
export interface TeamMemberModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  assigned?: boolean;
  assigned_at?: string;
  profile?: UserProfile;
  revoked_at?: string;
  team_id?: string;
  team_member_id?: string;
  tenant_id?: string;
  user_id?: string;
}

/** TeamModel schema */
export interface TeamModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  active?: boolean;
  description?: string;
  is_system?: boolean;
  member_count?: number;
  name?: string;
  reconciled_at?: string;
  team_id?: string;
  tenant_id?: string;
}

/** TeamRoleModel schema */
export interface TeamRoleModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  assigned?: boolean;
  assigned_at?: string;
  revoked_at?: string;
  role_id?: string;
  team_id?: string;
  team_role_id?: string;
  tenant_id?: string;
}

/** Template schema */
export interface Template {
  description?: string;
  name?: string;
  signal_event_type?: string;
  slug?: string;
  sql?: string;
  tags?: Array<string>;
  version?: number;
}

/** TenantModel schema */
export interface TenantModel {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  name?: string;
  slug?: string;
  tenant_id?: string;
}

/** Trigger schema */
export interface Trigger {
  event_space?: string;
  event_type?: string;
}

/** TriggerEvent schema */
export interface TriggerEvent {
  correlation_id?: string;
  event_id?: string;
  event_space?: string;
  event_type?: string;
  metadata?: Record<string, string>;
  occurred_at?: string;
  payload?: Record<string, string>;
  source?: string;
  tenant_id?: string;
}

/** TriggerWindow schema */
export interface TriggerWindow {
  duration_seconds?: number;
  threshold?: number;
}

/** UserProfile schema */
export interface UserProfile {
  /** Read-model revision for eventual-consistency polling */
  _rev?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  photo_url?: string;
  user_id?: string;
}

/** WaitingObservation schema */
export interface WaitingObservation {
  /** Whether the run is currently waiting on this dependency */
  active?: boolean;
  /** Number of wait/retry observations emitted during reconciliation */
  attempts?: number;
  /** Dependency or condition currently blocking progress */
  reason?: string;
  /** Catalog space associated with the wait, when known */
  space?: string;
}

/** addAccountsRequest schema */
export interface addAccountsRequest {
  accounts?: Array<CampaignAccount>;
}

/** addAccountsResult schema */
export interface addAccountsResult {
  created?: number;
  failed?: number;
  skipped?: number;
}

/** campaignRef schema */
export interface campaignRef {
  campaign_id?: string;
}

/** createCampaignRequest schema */
export interface createCampaignRequest {
  campaign_type?: string;
  description?: string;
  end_date?: string;
  name?: string;
  owner_id?: string;
  reviewer_type?: string;
  reviewers?: Array<string>;
  start_date?: string;
  targets?: Array<string>;
}

/** decisionRequest schema */
export interface decisionRequest {
  comments?: string;
  decision?: string;
  reason?: string;
  reviewed_by?: string;
}

/** engineEventIngestRequest schema */
export interface engineEventIngestRequest {
  data?: any;
  event_id?: string;
  event_type: string;
  occurred_at?: string;
}

/** engineEventTooLargeResponse schema */
export interface engineEventTooLargeResponse {
  detail?: string;
  title?: string;
}

/** evaluationRuleCreateOpenAPIDoc schema */
export interface evaluationRuleCreateOpenAPIDoc {
  EvaluationRule?: EvaluationRule;
}

/** evaluationSQLCompileOKResponse schema */
export interface evaluationSQLCompileOKResponse {
  diagnostics?: Array<evaluationSQLDiagnosticDTO>;
  rule?: EvaluationRule;
}

/** evaluationSQLDecompileResponse schema */
export interface evaluationSQLDecompileResponse {
  sql?: string;
}

/** evaluationSQLDiagnosticDTO schema */
export interface evaluationSQLDiagnosticDTO {
  code?: string;
  end?: evaluationSQLPositionDTO;
  message?: string;
  severity?: string;
  start?: evaluationSQLPositionDTO;
}

/** evaluationSQLFormatResponse schema */
export interface evaluationSQLFormatResponse {
  sql?: string;
}

/** evaluationSQLPositionDTO schema */
export interface evaluationSQLPositionDTO {
  column?: number;
  line?: number;
  offset?: number;
}

/** extendCampaignRequest schema */
export interface extendCampaignRequest {
  new_end_date?: string;
}

/** invocationResultResponse schema */
export interface invocationResultResponse {
  /** When the Sensitive Content row holding the sensitive result expires */
  expires_at?: string;
  /** The action's plain result */
  result?: any;
  /** Run sequence that produced the result */
  run_sequence?: number;
  /** ID (distinct_id) of the Sensitive Content row holding the sensitive result */
  sensitive_content_id?: string;
  /** Secret template the sensitive result matches; empty when it is free-form */
  template_name?: string;
}

/** resolveScopeRequest schema */
export interface resolveScopeRequest {
  total?: number;
}

/** resumeCursorResponse schema */
export interface resumeCursorResponse {
  /** Opaque connector-produced checkpoint, or null when none is stored */
  resume_cursor?: any;
  /** Run sequence that produced the cursor; 0 when none is stored */
  run_sequence?: number;
}

/** revertLaunchRequest schema */
export interface revertLaunchRequest {
  reason?: string;
}

/** revertRequest schema */
export interface revertRequest {
  reverted_by?: string;
  window_seconds?: number;
}

/** updateCampaignRequest schema */
export interface updateCampaignRequest {
  campaign_type?: string;
  description?: string;
  end_date?: string;
  name?: string;
  owner_id?: string;
  reviewer_type?: string;
  reviewers?: Array<string>;
  start_date?: string;
  targets?: Array<string>;
}

/** writeInvocationResultResponse schema */
export interface writeInvocationResultResponse {
  /** JSON pointers of the locations that failed the declared template; never the value */
  schema_error_paths?: Array<string>;
  /** True when a sealed result did not match its declared secret template and was stored free-form */
  template_mismatch?: boolean;
}
